// src/components/PrintingToolKit.js
// Small React utility to generate & print packing slips from orders
// Replicates the structure/logic of your Twig receipt template in JSX.

// ─── Imports ─────────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import jsPDF from 'jspdf';
import '../styles/toolkit-base.css';
import '../styles/lscOrderStatusChips.css';
import './PrintingToolKit.css';
import './PrintingToolKit.glass.css';
import { db } from '../services/firebase';
import { useAuth } from '../context/AuthContext';
import {
  PrinterOutlined,
  SettingOutlined,
  EyeOutlined,
  SearchOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import {
  collection,
  query as fsQuery,
  where,
  orderBy,
  limit,
  doc,
  setDoc,
  onSnapshot,
  addDoc,
  Timestamp,
  updateDoc
} from 'firebase/firestore';

import {
  buildOrderDeskPackingSlipHTML,
  createStandardPackingSlipMargins,
  normalizePackingSlipMargins,
  PACKING_SLIP_DEFAULT_LOGO_URL,
  PACKING_SLIP_ORDERDESK_DEFAULTS,
  withNormalizedPackingSlipMargins,
} from '../utils/packingSlipHtml';
import { enrichOrdersWithOrderDeskCheckoutDates } from '../utils/enrichOrdersWithOrderDeskCheckoutDates';
import {
  buildNormalizedOrderIdSet,
  isOrderInNormalizedIdSet,
} from '../services/orderDataService';
import { buildCheetahLz17PackingSlipHTML } from '../utils/cheetahLz17PackingSlipHtml';
import { buildGo4PrintDocuments } from '../utils/buildGo4PrintDocuments';
import {
  countPhysicalPagesByOrderFromHtml,
  printHtmlDocumentToPdf,
} from '../utils/printHtmlToPdf';
import { prepareHtmlForNativeBatchPrint } from '../utils/nativeBatchPrint';
import { printHTMLSilent } from '../utils/silentPrintService';
import { printHtmlBatchWithQz, withGiftCardTrayRotation } from '../services/qzPrintService';
import {
  buildPrintSeparatorHtml,
  countPrintGroupLabels,
  expandCopiesByOrderAliases,
  groupItemsForPrintSeparators,
  groupOrdersForPrintSeparators,
} from '../utils/printJobSeparators';
import WaveDeskPanel, { WAVE_COMMAND_LABELS } from './packingPrint/WaveDeskPanel';
import PrintRunMatrixPanel from './packingPrint/PrintRunMatrixPanel';
import ShippingLabelAttachReview from './packingPrint/ShippingLabelAttachReview';
import OrderPrintPartsDialog from './packingPrint/OrderPrintPartsDialog';
import { consumeShippingLabelAttachHandoff } from '../utils/shippingLabelAttachHandoff';
import { subscribeAttachedShippingLabels, subscribeDeskShippingLabelAttaches } from '../services/shippingLabelAttachService';
import {
  buildShipLabelOrderStatusMap,
  resolveShipLabelOrderStatus,
} from '../utils/shippingLabelOrderStatus';
import ShipLabelStatusChip from './packingPrint/ShipLabelStatusChip';
import { normalizePlannerViewsForPackers } from '../utils/packersPlannerViewUtils';
import PackingPrePrintDialog from './packingPrint/PackingPrePrintDialog';
import PrintHistoryModal from './packingPrint/PrintHistoryModal';
import PrintJobStatusDialog from './packingPrint/PrintJobStatusDialog';
import PrintQueuePanel from './packingPrint/PrintQueuePanel';
import GiftInscriptionPanel from './packingPrint/GiftInscriptionPanel';
import { usePrintQueue } from '../context/PrintQueueContext';
import { buildGiftCardPrintHtml } from '../utils/giftCardPrintHtml';
import { buildEmbeddedGiftCardHtml } from '../utils/printAssetInlining';
import {
  lookupInscriptionOrder,
  normalizeInscriptionIdentifier,
} from '../utils/inscriptionOrderLookup';
import ShippingLabelPdfPanel from './packingPrint/ShippingLabelPdfPanel';
import GeneralPrintPanel from './packingPrint/GeneralPrintPanel';
import PrintPreviewModal from './packingPrint/PrintPreviewModal';
import { drawPdfCopyBadge } from '../utils/pdfCopyBadge';
import {
  buildPrintingCompanyOptions,
  buildPrintingShippingMethodOptions,
  formatPrintingDeliveryDisplay,
  formatPrintingDispatchDisplay,
  getPrintingOrderCompany,
  getPrintingOrderDeliveryDay,
  getPrintingOrderShippingMethod,
  orderMatchesFolderDateScope,
} from '../utils/printingToolkitFilterOptions';
import {
  getOrderPrintSourceId,
  lookupPackingSlipPrintCount,
  recordPackingSlipPrintCounts,
  subscribePackingSlipPrintCounts,
} from '../services/packingSlipPrintCountService';
import {
  loadPackingSlipPrintPreferencesForOrders,
  preferenceDocIdForOrder,
} from '../services/packingSlipPrintPreferenceService';
import {
  coercePackingSlipFormat,
  isPackingSlipFormat,
  PACKING_SLIP_FORMAT,
  resolveBatchPackingSlipDefaults,
} from '../utils/packingSlipPrintDefaults';
import {
  fetchOrderDeskFolders,
  findAutoSelectOrderDeskFolder,
  isOrderDeskDateFolderName,
  orderDeskFolderNameToIsoDay,
} from '../services/orderDeskFolderOrdersService';
import { fetchOrderDeskFolderOrdersWithBridges } from '../services/orderDeskFolderOrdersWithBridges';
import { isWholesaleUnconfirmedOrder } from '../services/wholesalePendingFolderBridge';
import { XERO_WHOLESALE_FOLDER_NAME } from '../services/xeroWholesaleFolderBridge';
import {
  loadPrintingToolkitSession,
  PRINTING_TOOLKIT_MODES,
  savePrintingToolkitSession,
} from '../services/printingToolkitSessionStore';
import { buildPrintBenchLine, countCopiedSheets } from '../utils/printBenchSummary';
import { packDeskShortcut } from '../utils/printingToolkitPackShortcuts';

// ─── Config / constants ──────────────────────────────────────────────────────
const DEFAULT_LOGO_URL = PACKING_SLIP_DEFAULT_LOGO_URL;

// ─── Print Settings Defaults (Order Desk packing slip) ────────────────────────
const DEFAULT_PRINT_SETTINGS = {
  paperSize: 'A4', // A4 | A5 | A6 | Letter | label | custom
  orientation: 'portrait',
  margins: createStandardPackingSlipMargins(),
  // For paperSize === 'custom' (mm)
  customPaperMm: { width: 210, height: 297 },
  // For paperSize === 'label' (mm)
  labelPreset: '100x150', // common shipping label default
  labelMm: { width: 100, height: 150 },
  // Print scale (percent). Use when a printer driver is slightly off.
  scalePercent: 100,
  fontSize: PACKING_SLIP_ORDERDESK_DEFAULTS.fontSize,
  lineHeight: PACKING_SLIP_ORDERDESK_DEFAULTS.lineHeight,
  copiesPerOrder: 1,
  includeDuplicate: true,
  showPrices: false,
  showKgLines: true,
  logoUrl: DEFAULT_LOGO_URL,
  logoSize: PACKING_SLIP_ORDERDESK_DEFAULTS.logoWidth,
  logoWidth: PACKING_SLIP_ORDERDESK_DEFAULTS.logoWidth,
  headerColor: '#000000',
  footerText: PACKING_SLIP_ORDERDESK_DEFAULTS.footerText,
  showSignatureLines: true,
  showBarcode: false,
  watermark: '',
  customFields: []
};

const PAPER_SIZE_PRESETS_MM = {
  A4: { width: 210, height: 297 },
  A5: { width: 148, height: 210 },
  A6: { width: 105, height: 148 },
  Letter: { width: 215.9, height: 279.4 },
};

const LABEL_PRESETS_MM = {
  '100x150': { width: 100, height: 150 },      // Shipping label (common EU)
  '102x152': { width: 101.6, height: 152.4 },  // 4x6 inches
  '89x36': { width: 89, height: 36 },          // Small label
  '62x29': { width: 62, height: 29 },          // Dymo-ish small label
};

const normalizePrintSettings = (raw) => {
  const merged = withNormalizedPackingSlipMargins({
    ...DEFAULT_PRINT_SETTINGS,
    ...(raw || {}),
  });
  merged.margins = normalizePackingSlipMargins(merged.margins);
  merged.fontSize = Number(merged.fontSize) || DEFAULT_PRINT_SETTINGS.fontSize;
  merged.lineHeight = Number(merged.lineHeight) || DEFAULT_PRINT_SETTINGS.lineHeight;
  merged.logoSize = Number(merged.logoSize) || DEFAULT_PRINT_SETTINGS.logoSize;
  merged.copiesPerOrder = Math.max(1, parseInt(merged.copiesPerOrder, 10) || 1);
  merged.scalePercent = Math.max(10, Math.min(200, Number(merged.scalePercent) || 100));

  if (merged.paperSize === 'label') {
    const preset = LABEL_PRESETS_MM[merged.labelPreset];
    if (preset) merged.labelMm = { ...preset };
    merged.labelMm = {
      width: Number(merged.labelMm?.width) || DEFAULT_PRINT_SETTINGS.labelMm.width,
      height: Number(merged.labelMm?.height) || DEFAULT_PRINT_SETTINGS.labelMm.height,
    };
  }

  if (merged.paperSize === 'custom') {
    merged.customPaperMm = {
      width: Math.max(20, Number(merged.customPaperMm?.width) || DEFAULT_PRINT_SETTINGS.customPaperMm.width),
      height: Math.max(20, Number(merged.customPaperMm?.height) || DEFAULT_PRINT_SETTINGS.customPaperMm.height),
    };
  }

  return merged;
};

const resolvePageSizeMm = (settings) => {
  const s = normalizePrintSettings(settings);
  if (s.paperSize === 'custom') {
    return { ...s.customPaperMm };
  }
  if (s.paperSize === 'label') {
    return { ...s.labelMm };
  }
  return PAPER_SIZE_PRESETS_MM[s.paperSize] || PAPER_SIZE_PRESETS_MM.A4;
};

// ─── Print Templates ─────────────────────────────────────────────────────────
const PRINT_TEMPLATES = {
  standard: {
    name: 'Standard Packing Slip',
    description: 'Default packing slip with all standard fields',
    settings: { ...DEFAULT_PRINT_SETTINGS }
  },
  minimal: {
    name: 'Minimal Slip',
    description: 'Compact layout with essential information only',
    settings: { 
      ...DEFAULT_PRINT_SETTINGS, 
      fontSize: 8, 
      showSignatureLines: false,
      includeDuplicate: false
    }
  },
  detailed: {
    name: 'Detailed Slip',
    description: 'Comprehensive layout with prices and extra details',
    settings: { 
      ...DEFAULT_PRINT_SETTINGS, 
      showPrices: true, 
      fontSize: 10,
      showBarcode: true
    }
  },
  label: {
    name: 'Shipping Label',
    description: 'Address label format for shipping',
    settings: { 
      ...DEFAULT_PRINT_SETTINGS, 
      paperSize: 'label',
      labelPreset: '100x150',
      labelMm: { ...LABEL_PRESETS_MM['100x150'] },
      fontSize: 12,
      showSignatureLines: false,
      includeDuplicate: false
    }
  }
};

// Utility: safe text
const sanitize = (str) => String(str ?? '').replace(/\uFFFD/g, '').trim();

const orderMatchesSearchQuery = (order, query) => {
  const q = normalizeInscriptionIdentifier(query).toLowerCase();
  if (!q) return true;
  const checkout = order?.checkout_data || order?.checkoutData || {};
  return [
    order?.id,
    order?.orderId,
    order?.order_id,
    order?.order_number,
    order?.orderNumber,
    order?.source_id,
    order?.sourceId,
    checkout['Source ID#'],
    checkout['Order Number'],
    checkout['Customer Ref'],
    checkout.customer_ref,
  ].some((value) => {
    const normalized = normalizeInscriptionIdentifier(value).toLowerCase();
    return (
      normalized === q ||
      normalized.includes(q) ||
      String(value || '')
        .toLowerCase()
        .includes(q)
    );
  });
};

// Compute total quantity for an order
const getQuantityTotal = (order) => {
  const items = Array.isArray(order?.order_items) ? order.order_items : [];
  return items.reduce((sum, it) => sum + (Number(it.quantity) || 0), 0);
};

// Should show duplicate condensed copy?
const shouldPrintDuplicate = (order) => {
  const items = Array.isArray(order?.order_items) ? order.order_items : [];
  const qtyTotal = getQuantityTotal(order);
  return qtyTotal > 40 && items.length > 15;
};

/** Columns staff can click to sort the Orders table (Select / Actions stay fixed). */
const ORDERS_TABLE_SORTABLE = [
  { key: 'orderNumber', label: 'Order #' },
  { key: 'status', label: 'Status' },
  { key: 'printed', label: 'Printed' },
  { key: 'company', label: 'Company' },
  { key: 'dispatch', label: 'Dispatch' },
  { key: 'delivery', label: 'Delivery' },
  { key: 'shipping', label: 'Shipping' },
  { key: 'items', label: 'Items' },
  { key: 'qty', label: 'Qty total' },
  { key: 'duplicate', label: 'Duplicate?' },
];

const ORDERS_SORT_DEFAULT_DESC = new Set([
  'delivery',
  'dispatch',
  'printed',
  'qty',
  'items',
  'status',
]);

const comparePrintingOrderText = (a, b) =>
  String(a || '').localeCompare(String(b || ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });

// Build full HTML for given orders (Order Desk layout via shared helper)
async function buildPrintableHTMLForOrders(orders, logoUrlArg, settingsArg = DEFAULT_PRINT_SETTINGS) {
  const settings = normalizePrintSettings(settingsArg);
  const pageMm = resolvePageSizeMm(settings);
  const isLandscape = settings.orientation === 'landscape';
  const pageWidthMm = isLandscape ? pageMm.height : pageMm.width;
  const pageHeightMm = isLandscape ? pageMm.width : pageMm.height;
  const pageSizeCss =
    settings.paperSize === 'custom' || settings.paperSize === 'label'
      ? `${pageWidthMm}mm ${pageHeightMm}mm`
      : `${settings.paperSize} ${settings.orientation}`;
  return buildOrderDeskPackingSlipHTML(orders, {
    logoUrl: logoUrlArg || settings.logoUrl || DEFAULT_LOGO_URL,
    settings: {
      ...settings,
      pageSizeCss,
      logoWidth: settings.logoWidth || settings.logoSize || PACKING_SLIP_ORDERDESK_DEFAULTS.logoWidth,
    },
  });
}

// Render full HTML (with possibly multiple orders) to a PDF with per-order paging
async function generatePdfFromHtml(fullHtml, settingsArg = {}) {
  const sharedSettings = normalizePrintSettings(settingsArg);
  const sharedPageMm = resolvePageSizeMm(sharedSettings);
  const sharedLandscape = sharedSettings.orientation === 'landscape';
  const sharedPageWidthMm = sharedLandscape ? sharedPageMm.height : sharedPageMm.width;
  const sharedPageHeightMm = sharedLandscape ? sharedPageMm.width : sharedPageMm.height;
  const sharedMmToPt = (mm) => (mm * 72) / 25.4;
  const sharedMmToPx = (mm) => (mm * 96) / 25.4;
  return printHtmlDocumentToPdf(fullHtml, {
    fileName: `packing-slips-${new Date().toISOString().substring(0, 10)}.pdf`,
    orientation: sharedLandscape ? 'l' : 'p',
    pageFormat: [
      sharedMmToPt(sharedPageWidthMm),
      sharedMmToPt(sharedPageHeightMm),
    ],
    hostWidthPx: Math.max(120, Math.round(sharedMmToPx(sharedPageWidthMm))),
    copiesPerOrder: sharedSettings.copiesPerOrder,
    copiesByOrder: sharedSettings.copiesByOrder,
  });
}

// eslint-disable-next-line no-unused-vars
async function generatePdfFromHtmlRasterLegacy(fullHtml, settingsArg = {}) {
  const { default: html2canvas } = await import('html2canvas');

  const settings = normalizePrintSettings(settingsArg);
  const pageMm = resolvePageSizeMm(settings);
  const isLandscape = settings.orientation === 'landscape';
  const pageWidthMm = isLandscape ? pageMm.height : pageMm.width;
  const pageHeightMm = isLandscape ? pageMm.width : pageMm.height;
  const mmToPt = (mm) => (mm * 72) / 25.4;
  const mmToPx = (mm) => (mm * 96) / 25.4;
  const hostWidthPx = Math.max(120, Math.round(mmToPx(pageWidthMm)));
  const pdfFormat = [mmToPt(pageWidthMm), mmToPt(pageHeightMm)];

  // Force a real right inset in the capture (html2canvas is unreliable with mm padding alone)
  const slipMargins = normalizePackingSlipMargins(settings.margins);
  const padTop = Math.round(mmToPx(slipMargins.top));
  const padRight = Math.round(mmToPx(slipMargins.right));
  const padBottom = Math.round(mmToPx(slipMargins.bottom));
  const padLeft = Math.round(mmToPx(slipMargins.left));
  const captureCssBoost = `
.print-root { transform: none !important; width: ${hostWidthPx}px !important; max-width: ${hostWidthPx}px !important; }
.main, .main .wrapper, .main .wrapper-table { overflow: visible !important; width: 100% !important; max-width: 100% !important; box-sizing: border-box !important; }
.main .wrapper-table { padding: ${padTop}px ${padRight}px ${padBottom}px ${padLeft}px !important; }
.header_logo { overflow: visible !important; }
.header_logo .order-qr svg { width: 52px !important; max-width: 52px !important; height: 52px !important; }
`;

  let css = '';
  let body = fullHtml;
  try {
    const cssMatch = fullHtml.match(/<style>([\s\S]*?)<\/style>/i);
    if (cssMatch) css = cssMatch[1];
    const bodyMatch = fullHtml.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    if (bodyMatch) body = bodyMatch[1];
  } catch {}

  const host = document.createElement('div');
  host.style.position = 'fixed';
  host.style.left = '-99999px';
  host.style.top = '0';
  host.style.width = `${hostWidthPx}px`;
  host.style.background = '#fff';
  host.innerHTML = `<style>${css}${captureCssBoost}</style>${body}`;
  document.body.appendChild(host);

  const blocks = Array.from(host.querySelectorAll('.main'));
  const pdf = new jsPDF({ orientation: settings.orientation === 'landscape' ? 'l' : 'p', unit: 'pt', format: pdfFormat });
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-99999px';
    container.style.top = '0';
    container.style.width = `${hostWidthPx}px`;
    container.style.background = '#fff';
    container.style.overflow = 'visible';
    container.innerHTML = `<style>${css}${captureCssBoost}</style><div class="print-root">${block.outerHTML}</div>`;
    document.body.appendChild(container);

    const imgs = Array.from(container.querySelectorAll('img'));
    await Promise.all(
      imgs.map((img) => new Promise((resolve) => {
        if (img.complete) return resolve();
        img.onload = img.onerror = () => resolve();
      }))
    );

    const canvas = await html2canvas(container, {
      useCORS: true,
      scale: 2,
      backgroundColor: '#ffffff',
      logging: false,
      windowWidth: hostWidthPx,
      width: hostWidthPx,
      scrollX: 0,
      scrollY: 0,
    });

    const imgData = canvas.toDataURL('image/png');
    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    const copies = Math.max(1, parseInt(settings.copiesPerOrder, 10) || 1);
    for (let c = 1; c <= copies; c++) {
      if (i > 0 || c > 1) pdf.addPage();

      let heightLeft = imgHeight;
      let position = 0;
      // Full page instance for this copy
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
      drawPdfCopyBadge(pdf, c, copies);
      heightLeft -= pdfHeight;
      while (heightLeft > 0) {
        position -= pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
        drawPdfCopyBadge(pdf, c, copies);
        heightLeft -= pdfHeight;
      }
    }

    document.body.removeChild(container);
  }

  document.body.removeChild(host);
  pdf.save(`packing-slips-${new Date().toISOString().substring(0,10)}.pdf`);
}

export async function generatePackingSlipsFromOrders(orders, { logoUrl: customLogo } = {}) {
  const logo = customLogo || DEFAULT_LOGO_URL;
  const list = Array.isArray(orders) ? orders : [orders];
  const enriched = await enrichOrdersWithOrderDeskCheckoutDates(list, { prefer: 'both' });
  const html = await buildPrintableHTMLForOrders(enriched, logo, DEFAULT_PRINT_SETTINGS);
  await generatePdfFromHtml(html, DEFAULT_PRINT_SETTINGS);
}

// ─── Component ───────────────────────────────────────────────────────────────
const PrintingToolKit = ({
  logoUrl = DEFAULT_LOGO_URL,
  incomingOrders = null, // optional: pass orders directly to skip OrderDesk folder load
}) => {
  const printQueue = usePrintQueue();
  const { user } = useAuth();
  const initialSessionRef = useRef(null);
  if (!initialSessionRef.current) {
    initialSessionRef.current = loadPrintingToolkitSession();
  }
  const initialSession = initialSessionRef.current;
  
  // Local state (restored from local session so mode switches do not wipe work)
  const [orders, setOrders] = useState([]);
  const [availableFolders, setAvailableFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(
    initialSession.selectedFolderId || '',
  );
  const [dateFilterField, setDateFilterField] = useState(
    initialSession.dateFilterField === 'dispatch'
      ? 'dispatch'
      : initialSession.dateFilterField === 'delivery'
        ? 'delivery'
        : 'all',
  );
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [orderLoadStatus, setOrderLoadStatus] = useState('');
  const [filterShipping, setFilterShipping] = useState(initialSession.filterShipping || ''); // '' = all
  const [filterCompany, setFilterCompany] = useState(initialSession.filterCompany || ''); // '' = all
  const [searchId, setSearchId] = useState(initialSession.searchId || '');
  const [selectedIds, setSelectedIds] = useState(initialSession.selectedIds || {});
  const [orderCopiesBySource, setOrderCopiesBySource] = useState(initialSession.orderCopies || {});
  const [isLoading, setIsLoading] = useState(false);
  const didAutoSelectFolderRef = useRef(Boolean(initialSession.selectedFolderId));
  
  // Top-level modes: Pack | Other paper | Queue
  const [toolkitMode, setToolkitMode] = useState(
    initialSession.mode || PRINTING_TOOLKIT_MODES.PACK,
  );
  const [otherTab, setOtherTab] = useState(initialSession.otherTab || 'labels');

  useEffect(() => {
    const openLabelsFromHandoff = () => {
      setToolkitMode(PRINTING_TOOLKIT_MODES.OTHER);
      setOtherTab('labels');
    };
    window.addEventListener('lsc:shipping-label-pdf-handoff', openLabelsFromHandoff);
    return () =>
      window.removeEventListener('lsc:shipping-label-pdf-handoff', openLabelsFromHandoff);
  }, []);

  const [generalSizeKey, setGeneralSizeKey] = useState(initialSession.generalSizeKey || 'A4');
  const [generalCopies, setGeneralCopies] = useState(initialSession.generalCopies || 1);
  const [generalPageRanges, setGeneralPageRanges] = useState(
    initialSession.generalPageRanges || '',
  );
  const [generalNotes, setGeneralNotes] = useState(initialSession.generalNotes || '');
  const [printSettings, setPrintSettings] = useState(DEFAULT_PRINT_SETTINGS);
  const [savedSettings, setSavedSettings] = useState([]);
  const [printTemplates, setPrintTemplates] = useState(PRINT_TEMPLATES);
  const [printHistory, setPrintHistory] = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  /** Running print-run counts keyed by sourceId__deliveryYmd */
  const [printCountMap, setPrintCountMap] = useState({});
  /** Same Firestore packed/shipped doc ids Packers uses (live). */
  const [packedIds, setPackedIds] = useState(() => new Set());
  const [shippedIds, setShippedIds] = useState(() => new Set());
  /** Orders table: click column headers to sort (default delivery, newest first). */
  const [ordersSort, setOrdersSort] = useState({ key: 'delivery', dir: 'desc' });
  
  // Modal states
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showNarrowFilters, setShowNarrowFilters] = useState(false);
  const [showSetupMenu, setShowSetupMenu] = useState(false);
  const [previewOrder, setPreviewOrder] = useState(null);
  const [prePrintRequest, setPrePrintRequest] = useState(null);
  const [prePrintBusy, setPrePrintBusy] = useState(false);
  const [printJobStatus, setPrintJobStatus] = useState(null);
  const [printBench, setPrintBench] = useState(null);
  const [pendingPrintAudit, setPendingPrintAudit] = useState(null);
  const [orderLookupBusy, setOrderLookupBusy] = useState(false);
  const [orderLookupStatus, setOrderLookupStatus] = useState('');
  
  // Batch printing state
  const [batchSettings, setBatchSettings] = useState({
    groupBy: 'none', // none, shipping, date, company
    sortBy: 'orderNumber', // orderNumber, date, company
    separatePages: true,
    includeIndex: true
  });

  const listRef = useRef(null);
  const findInputRef = useRef(null);
  const [teamAttaches, setTeamAttaches] = useState([]);
  const [deskShipLabelAttaches, setDeskShipLabelAttaches] = useState([]);
  const waveDeskRef = useRef(null);
  const waveDeskCardRef = useRef(null);
  const [attachReview, setAttachReview] = useState(null);
  const [orderPartsTarget, setOrderPartsTarget] = useState(null);
  const [plannerViewsRaw, setPlannerViewsRaw] = useState([]);
  const plannerViews = useMemo(
    () => normalizePlannerViewsForPackers(plannerViewsRaw).views,
    [plannerViewsRaw],
  );

  useEffect(() => subscribeAttachedShippingLabels(setTeamAttaches), []);
  useEffect(() => subscribeDeskShippingLabelAttaches(setDeskShipLabelAttaches), []);

  const shipLabelStatusMap = useMemo(
    () => buildShipLabelOrderStatusMap(deskShipLabelAttaches),
    [deskShipLabelAttaches],
  );

  const openWaveDeskForShipLabels = useCallback(() => {
    setToolkitMode(PRINTING_TOOLKIT_MODES.PACK);
    window.requestAnimationFrame(() => {
      waveDeskCardRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    });
  }, []);
  useEffect(() => {
    const ref = doc(db, 'settings', 'production');
    return onSnapshot(
      ref,
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        setPlannerViewsRaw(Array.isArray(data.plannerViews) ? data.plannerViews : []);
      },
      () => setPlannerViewsRaw([]),
    );
  }, []);

  useEffect(() => {
    const consume = () => {
      const handoff = consumeShippingLabelAttachHandoff();
      if (!handoff?.blob) return;
      setToolkitMode(PRINTING_TOOLKIT_MODES.PACK);
      setAttachReview({
        blob: handoff.blob,
        fileName: handoff.fileName,
        driveItemId: handoff.driveItemId || '',
      });
    };
    consume();
    if (typeof window === 'undefined') return undefined;
    window.addEventListener('lsc:shipping-label-attach-handoff', consume);
    return () => window.removeEventListener('lsc:shipping-label-attach-handoff', consume);
  }, []);

  const selectedFolder = useMemo(
    () =>
      (availableFolders || []).find(
        (folder) => String(folder.id) === String(selectedFolderId),
      ) || null,
    [availableFolders, selectedFolderId],
  );

  const printCountDay = useMemo(
    () => orderDeskFolderNameToIsoDay(selectedFolder?.name) || '',
    [selectedFolder],
  );

  // Persist toolkit session locally for continuity across modes / refresh
  useEffect(() => {
    savePrintingToolkitSession({
      mode: toolkitMode,
      otherTab,
      selectedFolderId,
      dateFilterField,
      filterDate: printCountDay,
      filterShipping,
      filterCompany,
      searchId,
      selectedIds,
      orderCopies: orderCopiesBySource,
      generalSizeKey,
      generalCopies,
      generalPageRanges,
      generalNotes,
    });
  }, [
    toolkitMode,
    otherTab,
    selectedFolderId,
    dateFilterField,
    printCountDay,
    filterShipping,
    filterCompany,
    searchId,
    selectedIds,
    orderCopiesBySource,
    generalSizeKey,
    generalCopies,
    generalPageRanges,
    generalNotes,
  ]);

  const openQueueMode = useCallback(() => {
    setToolkitMode(PRINTING_TOOLKIT_MODES.QUEUE);
  }, []);

  const patchGeneralSession = useCallback((partial) => {
    if (partial.generalSizeKey != null) setGeneralSizeKey(partial.generalSizeKey);
    if (partial.generalCopies != null) setGeneralCopies(partial.generalCopies);
    if (partial.generalPageRanges != null) setGeneralPageRanges(partial.generalPageRanges);
    if (partial.generalNotes != null) setGeneralNotes(partial.generalNotes);
  }, []);

  // Load OrderDesk folders (Packers parity)
  useEffect(() => {
    if (Array.isArray(incomingOrders)) return undefined;
    let cancelled = false;
    const loadFolders = async () => {
      setFoldersLoading(true);
      try {
        const folders = await fetchOrderDeskFolders();
        if (cancelled) return;
        setAvailableFolders(folders);
        const restored = folders.find(
          (folder) => String(folder.id) === String(selectedFolderId),
        );
        if (restored) {
          didAutoSelectFolderRef.current = true;
          return;
        }
        if (!didAutoSelectFolderRef.current) {
          const auto = findAutoSelectOrderDeskFolder(folders);
          if (auto) setSelectedFolderId(String(auto.id));
          didAutoSelectFolderRef.current = true;
        } else if (selectedFolderId && !restored) {
          const auto = findAutoSelectOrderDeskFolder(folders);
          if (auto) setSelectedFolderId(String(auto.id));
        }
      } catch (error) {
        console.error('[PrintingToolKit] Failed to load OrderDesk folders:', error);
        if (!cancelled) {
          setAvailableFolders([]);
          setOrderLoadStatus(
            error?.message || 'Failed to load OrderDesk folders.',
          );
        }
      } finally {
        if (!cancelled) setFoldersLoading(false);
      }
    };
    loadFolders();
    return () => {
      cancelled = true;
    };
    // selectedFolderId intentionally omitted: restore once from initial session
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingOrders]);

  const loadFolderOrders = useCallback(async () => {
    if (Array.isArray(incomingOrders)) {
      setOrders(incomingOrders);
      return;
    }
    if (!selectedFolder) {
      setOrders([]);
      return;
    }
    setIsLoading(true);
    setOrderLoadStatus(
      `Loading everyone in OrderDesk folder ${selectedFolder.name} (plus Sent to Xero / Wholesale pending)…`,
    );
    try {
      const {
        orders: list,
        membershipCount,
        xeroLinkedCount,
        pendingLinkedCount,
        notes,
      } = await fetchOrderDeskFolderOrdersWithBridges({
        folder: selectedFolder,
        folders: availableFolders,
        scope: 'membership',
      });
      setOrders(list);
      setSelectedIds({});
      const bridgeBits = notes.length ? ` · ${notes.join('; ')}` : '';
      setOrderLoadStatus(
        `${list.length} order${list.length === 1 ? '' : 's'} for ${selectedFolder.name} (${membershipCount} in folder${bridgeBits})`,
      );
      if (xeroLinkedCount || pendingLinkedCount) {
        console.log('[PrintingToolKit] Folder bridges:', {
          membershipCount,
          xeroLinkedCount,
          pendingLinkedCount,
        });
      }
    } catch (error) {
      console.error('[PrintingToolKit] Failed to load OrderDesk folder orders:', error);
      setOrders([]);
      setOrderLoadStatus(
        error?.message || `Failed to load orders for ${selectedFolder.name}.`,
      );
    } finally {
      setIsLoading(false);
    }
  }, [incomingOrders, selectedFolder, availableFolders]);

  useEffect(() => {
    loadFolderOrders();
  }, [loadFolderOrders]);

  // Load user print settings and history
  useEffect(() => {
    if (!user?.uid) return;

    // Load saved print settings
    const settingsUnsub = onSnapshot(
      doc(db, 'userPrintSettings', user.uid),
      (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          setPrintSettings(normalizePrintSettings(data.settings));
          setSavedSettings(data.savedSettings || []);
        }
      }
    );

    // Load print history
    const historyUnsub = onSnapshot(
      fsQuery(
        collection(db, 'printHistory'),
        where('userId', '==', user.uid),
        orderBy('createdAt', 'desc'),
        limit(100)
      ),
      (snapshot) => {
        const history = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPrintHistory(history);
      },
      (err) => {
        console.warn('[PrintingToolKit] printHistory listener unavailable (index may still be building):', err?.code || err);
      }
    );

    // Load custom templates
    const templatesUnsub = onSnapshot(
      fsQuery(
        collection(db, 'printTemplates'),
        where('userId', '==', user.uid),
        orderBy('createdAt', 'desc')
      ),
      (snapshot) => {
        const templates = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setCustomTemplates(templates);
      },
      (err) => {
        console.warn('[PrintingToolKit] printTemplates listener unavailable (index may still be building):', err?.code || err);
      }
    );

    return () => {
      settingsUnsub();
      historyUnsub();
      templatesUnsub();
    };
  }, [user?.uid]);

  // Live packed / shipped status (same collections Packers writes)
  useEffect(() => {
    const packedUnsub = onSnapshot(
      collection(db, 'packedOrders'),
      (snap) => {
        const next = new Set();
        snap.docs.forEach((d) => {
          next.add(d.id);
          const stripped = String(d.id || '')
            .trim()
            .replace(/^#/, '')
            .replace(/^--+/, '');
          if (stripped) next.add(stripped);
        });
        setPackedIds(next);
      },
      (err) => {
        console.warn('[PrintingToolKit] packedOrders listener unavailable:', err?.code || err);
      },
    );
    const shippedUnsub = onSnapshot(
      collection(db, 'shippedOrders'),
      (snap) => {
        const next = new Set();
        snap.docs.forEach((d) => {
          next.add(d.id);
          const stripped = String(d.id || '')
            .trim()
            .replace(/^#/, '')
            .replace(/^--+/, '');
          if (stripped) next.add(stripped);
        });
        setShippedIds(next);
      },
      (err) => {
        console.warn('[PrintingToolKit] shippedOrders listener unavailable:', err?.code || err);
      },
    );
    return () => {
      packedUnsub();
      shippedUnsub();
    };
  }, []);

  const packedNorm = useMemo(() => buildNormalizedOrderIdSet(packedIds), [packedIds]);
  const shippedNorm = useMemo(() => buildNormalizedOrderIdSet(shippedIds), [shippedIds]);

  const isOrderPacked = useCallback(
    (order) => isOrderInNormalizedIdSet(order, packedNorm),
    [packedNorm],
  );
  const isOrderShipped = useCallback(
    (order) => isOrderInNormalizedIdSet(order, shippedNorm),
    [shippedNorm],
  );

  // Live print counts for orders in view (per source id + folder delivery day)
  useEffect(() => {
    const sourceIds = (orders || []).map((o) => getOrderPrintSourceId(o)).filter(Boolean);
    const unsub = subscribePackingSlipPrintCounts(sourceIds, setPrintCountMap, printCountDay || '');
    return () => {
      if (typeof unsub === 'function') unsub();
    };
  }, [orders, printCountDay]);

  useEffect(() => {
    if (
      (dateFilterField === 'dispatch' || dateFilterField === 'delivery') &&
      selectedFolder &&
      !isOrderDeskDateFolderName(selectedFolder.name)
    ) {
      setDateFilterField('all');
    }
  }, [selectedFolder, dateFilterField]);

  // Cascading options: shipping / company from the loaded folder set
  const shippingMethodOptions = useMemo(
    () => buildPrintingShippingMethodOptions(orders),
    [orders],
  );
  const companyOptions = useMemo(
    () => buildPrintingCompanyOptions(orders),
    [orders],
  );

  // Drop shipping/company filters that are no longer in the scoped option lists
  useEffect(() => {
    if (filterShipping && filterShipping !== 'Unspecified' && !shippingMethodOptions.includes(filterShipping)) {
      setFilterShipping('');
    }
    if (filterShipping === 'Unspecified' && !shippingMethodOptions.includes('Unspecified')) {
      setFilterShipping('');
    }
    if (filterCompany && filterCompany !== 'Unspecified' && !companyOptions.includes(filterCompany)) {
      setFilterCompany('');
    }
    if (filterCompany === 'Unspecified' && !companyOptions.includes('Unspecified')) {
      setFilterCompany('');
    }
  }, [shippingMethodOptions, companyOptions, filterShipping, filterCompany]);

  // Filtered view — order # / source search matches across id fields and
  // bypasses shipping/company filters so a known ID is always findable.
  const filteredOrders = useMemo(() => {
    const query = String(searchId || '').trim();
    return (orders || []).filter((o) => {
      if (query) {
        if (!orderMatchesSearchQuery(o, query)) return false;
        return true;
      }
      if (
        !orderMatchesFolderDateScope(
          o,
          selectedFolder?.name,
          dateFilterField,
        )
      ) {
        return false;
      }
      if (filterShipping) {
        const method = getPrintingOrderShippingMethod(o);
        if (filterShipping === 'Unspecified') {
          if (method) return false;
        } else if (method !== filterShipping) {
          return false;
        }
      }
      if (filterCompany) {
        const company = getPrintingOrderCompany(o);
        if (filterCompany === 'Unspecified') {
          if (company) return false;
        } else if (company !== filterCompany) {
          return false;
        }
      }
      return true;
    });
  }, [
    orders,
    filterShipping,
    filterCompany,
    searchId,
    selectedFolder?.name,
    dateFilterField,
  ]);

  const handleOrdersSort = useCallback((key) => {
    setOrdersSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      return {
        key,
        dir: ORDERS_SORT_DEFAULT_DESC.has(key) ? 'desc' : 'asc',
      };
    });
  }, []);

  const sortedFilteredOrders = useMemo(() => {
    const list = filteredOrders.slice();
    const { key, dir } = ordersSort;
    const mul = dir === 'asc' ? 1 : -1;
    const statusRank = (order) => {
      if (isOrderShipped(order)) return 2;
      if (isOrderPacked(order)) return 1;
      return 0;
    };
    list.sort((a, b) => {
      let cmp = 0;
      switch (key) {
        case 'orderNumber':
          cmp = comparePrintingOrderText(a.source_id || a.id, b.source_id || b.id);
          break;
        case 'status':
          cmp = statusRank(a) - statusRank(b);
          break;
        case 'printed':
          cmp =
            lookupPackingSlipPrintCount(printCountMap, a) -
            lookupPackingSlipPrintCount(printCountMap, b);
          break;
        case 'company':
          cmp = comparePrintingOrderText(
            getPrintingOrderCompany(a),
            getPrintingOrderCompany(b),
          );
          break;
        case 'dispatch':
          cmp = comparePrintingOrderText(
            a?.dispatchDate || getPrintingOrderDeliveryDay(a),
            b?.dispatchDate || getPrintingOrderDeliveryDay(b),
          );
          break;
        case 'delivery':
          cmp = comparePrintingOrderText(
            getPrintingOrderDeliveryDay(a),
            getPrintingOrderDeliveryDay(b),
          );
          break;
        case 'shipping':
          cmp = comparePrintingOrderText(
            getPrintingOrderShippingMethod(a),
            getPrintingOrderShippingMethod(b),
          );
          break;
        case 'items': {
          const ia = Array.isArray(a?.order_items) ? a.order_items.length : 0;
          const ib = Array.isArray(b?.order_items) ? b.order_items.length : 0;
          cmp = ia - ib;
          break;
        }
        case 'qty':
          cmp = getQuantityTotal(a) - getQuantityTotal(b);
          break;
        case 'duplicate':
          cmp = (shouldPrintDuplicate(a) ? 1 : 0) - (shouldPrintDuplicate(b) ? 1 : 0);
          break;
        default:
          break;
      }
      if (cmp !== 0) return cmp * mul;
      return comparePrintingOrderText(a.source_id || a.id, b.source_id || b.id);
    });
    return list;
  }, [
    filteredOrders,
    ordersSort,
    printCountMap,
    isOrderPacked,
    isOrderShipped,
  ]);

  const lookupPrintingInscriptionOrder = useCallback(
    async (identifier) =>
      lookupInscriptionOrder(identifier, { localOrders: orders }),
    [orders],
  );

  const toggleAll = (checked) => {
    if (checked) {
      const next = {};
      filteredOrders.forEach((o) => (next[o.id] = true));
      setSelectedIds(next);
    } else {
      setSelectedIds({});
    }
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const selectedList = useMemo(
    () => filteredOrders.filter((o) => selectedIds[o.id]),
    [filteredOrders, selectedIds]
  );

  const buildCopiesByOrder = useCallback(
    (orderList, defaultCopies = 1) => {
      const fallback = Math.max(1, parseInt(defaultCopies, 10) || 1);
      return (orderList || []).reduce((map, order) => {
        const sourceId = getOrderPrintSourceId(order);
        if (!sourceId) return map;
        map[sourceId] = Math.max(
          1,
          parseInt(orderCopiesBySource[sourceId], 10) || fallback,
        );
        return map;
      }, {});
    },
    [orderCopiesBySource],
  );

  const openPrePrintDialog = useCallback(
    async (ordersToPrint = null, initialFormat = null, settingsOverride = null) => {
      const list =
        ordersToPrint || (selectedList.length ? selectedList : filteredOrders);
      if (!list.length) {
        alert('No orders to print. Select some or adjust filters.');
        return;
      }
      const formatLocked = isPackingSlipFormat(initialFormat);
      let format = formatLocked ? initialFormat : PACKING_SLIP_FORMAT.STANDARD;
      let copiesByOrder = buildCopiesByOrder(list, printSettings.copiesPerOrder);
      let defaultsNote = '';
      try {
        const preferences = await loadPackingSlipPrintPreferencesForOrders(list);
        const smart = resolveBatchPackingSlipDefaults(
          list,
          preferences,
          preferenceDocIdForOrder,
        );
        if (!formatLocked) format = smart.format;
        copiesByOrder = { ...smart.copiesByOrder };
        list.forEach((order) => {
          const sourceId = getOrderPrintSourceId(order);
          if (!sourceId) return;
          const sessionCopies = parseInt(orderCopiesBySource[sourceId], 10);
          if (Number.isFinite(sessionCopies) && sessionCopies >= 1) {
            copiesByOrder[sourceId] = sessionCopies;
          } else if (!copiesByOrder[sourceId]) {
            copiesByOrder[sourceId] = Math.max(
              1,
              parseInt(printSettings.copiesPerOrder, 10) || 1,
            );
          }
        });
        defaultsNote = formatLocked
          ? 'Copies from customer + method history (or size). Format from this wave recipe.'
          : 'Format and copies from shipping method, markets cues, and prior prints for the same customer + method. Change anything before Print now.';
      } catch (error) {
        console.warn('[PrintingToolKit] smart packing defaults failed:', error);
      }
      setPrePrintRequest({
        orders: list,
        initialFormat: coercePackingSlipFormat(format),
        settings: settingsOverride || null,
        copiesByOrder,
        defaultsNote,
      });
    },
    [
      selectedList,
      filteredOrders,
      buildCopiesByOrder,
      printSettings.copiesPerOrder,
      orderCopiesBySource,
    ],
  );

  useEffect(() => {
    const onKeyDown = (event) => {
      if (
        prePrintRequest ||
        printJobStatus ||
        showSettingsModal ||
        showTemplateModal ||
        showHistoryModal ||
        showPreviewModal ||
        showBatchModal
      ) {
        return;
      }
      if (toolkitMode !== PRINTING_TOOLKIT_MODES.PACK) return;
      const shortcut = packDeskShortcut(event);
      if (shortcut === 'find') {
        event.preventDefault();
        findInputRef.current?.focus();
        return;
      }
      if (shortcut === 'print') {
        event.preventDefault();
        openPrePrintDialog();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    prePrintRequest,
    printJobStatus,
    showSettingsModal,
    showTemplateModal,
    showHistoryModal,
    showPreviewModal,
    showBatchModal,
    toolkitMode,
    openPrePrintDialog,
  ]);

  const findAndPrintOrderByIdentifier = useCallback(async () => {
    const identifier = normalizeInscriptionIdentifier(searchId);
    if (!identifier) {
      setOrderLookupStatus('Enter an order number or source ID first.');
      return;
    }
    setOrderLookupBusy(true);
    setOrderLookupStatus(`Looking up ${identifier}…`);
    try {
      const found = await lookupInscriptionOrder(identifier, {
        localOrders: orders,
      });
      if (!found) {
        setOrderLookupStatus(
          `No order found for "${identifier}" in the loaded list or OrderDesk.`,
        );
        return;
      }
      const orderKey = getOrderPrintSourceId(found) || found.id || identifier;
      const existing = (orders || []).find(
        (order) =>
          orderMatchesSearchQuery(order, identifier) ||
          orderMatchesSearchQuery(order, orderKey),
      );
      const listedId = existing?.id || found.id || orderKey;
      const orderForPrint = { ...(existing || found), id: listedId };
      if (!existing) {
        setOrders((previous) => [orderForPrint, ...(previous || [])]);
      }
      setSelectedIds({ [listedId]: true });
      setFilterShipping('');
      setFilterCompany('');
      setSearchId(orderKey);
      setOrderLookupStatus(
        found._inscriptionSource === 'orderDesk'
          ? `Found ${orderKey} via OrderDesk. Opening print setup…`
          : `Found ${orderKey}. Opening print setup…`,
      );
      openPrePrintDialog([orderForPrint]);
    } catch (error) {
      console.error('[PrintingToolKit] Order lookup failed:', error);
      setOrderLookupStatus(
        error?.message || `Lookup failed for "${identifier}".`,
      );
    } finally {
      setOrderLookupBusy(false);
    }
  }, [searchId, orders, openPrePrintDialog]);

  const prePrintInitialCopies = useMemo(
    () =>
      prePrintRequest?.copiesByOrder
        ? prePrintRequest.copiesByOrder
        : prePrintRequest
          ? buildCopiesByOrder(
              prePrintRequest.orders,
              printSettings.copiesPerOrder,
            )
          : {},
    [prePrintRequest, buildCopiesByOrder, printSettings.copiesPerOrder],
  );

  // ─── Enhanced Handler Functions ─────────────────────────────────────────────
  
  // Save print settings
  const savePrintSettings = useCallback(async (settings, name = null) => {
    if (!user?.uid) return;
    
    try {
      const normalized = normalizePrintSettings(settings);
      const settingsData = {
        settings: normalized,
        savedSettings: name ? [...savedSettings, { name, settings: normalized, createdAt: new Date() }] : savedSettings,
        updatedAt: Timestamp.now()
      };
      
      await setDoc(doc(db, 'userPrintSettings', user.uid), settingsData, { merge: true });
      setPrintSettings(normalized);
      
      if (name) {
        setSavedSettings(settingsData.savedSettings);
        alert(`Print settings saved as "${name}"`);
      }
    } catch (error) {
      console.error('Error saving print settings:', error);
      alert('Failed to save print settings');
    }
  }, [user?.uid, savedSettings]);

  // Load saved settings
  const loadSavedSettings = useCallback((settingsName) => {
    const saved = savedSettings.find(s => s.name === settingsName);
    if (saved) {
      setPrintSettings(normalizePrintSettings(saved.settings));
    }
  }, [savedSettings]);

  // Create custom template
  const createCustomTemplate = useCallback(async (templateData) => {
    if (!user?.uid) return;
    
    try {
      await addDoc(collection(db, 'printTemplates'), {
        ...templateData,
        userId: user.uid,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      alert(`Template "${templateData.name}" created successfully`);
      setShowTemplateModal(false);
    } catch (error) {
      console.error('Error creating template:', error);
      alert('Failed to create template');
    }
  }, [user?.uid]);

  // Record print job in history + per-order weekly running count
  const recordPrintJob = useCallback(async (
    orders,
    settings,
    jobType = 'packing_slip',
    physicalSheetsByOrder = {},
    copiesByOrder = {},
  ) => {
    if (!user?.uid) return { historyRecorded: false, countRecorded: false };
    const copies = Math.max(1, parseInt(settings?.copiesPerOrder, 10) || 1);
    let historyRecorded = false;
    let countRecorded = false;

    try {
      await addDoc(collection(db, 'printHistory'), {
        userId: user.uid,
        jobType: jobType,
        orderCount: orders.length,
        orderIds: orders.map(o => o.id),
        sourceIds: orders.map((o) => getOrderPrintSourceId(o)).filter(Boolean),
        copiesPerOrder: copies,
        copiesByOrder,
        settings: settings,
        createdAt: Timestamp.now(),
        status: 'completed'
      });
      historyRecorded = true;
    } catch (error) {
      console.error('Error recording print job:', error);
    }

    try {
      await recordPackingSlipPrintCounts(orders, {
        copies,
        jobType,
        userId: user.uid,
        physicalSheetsByOrder,
        copiesByOrder,
      });
      countRecorded = true;
    } catch (error) {
      console.warn('[PrintingToolKit] print count update failed (rules may block):', error?.code || error);
    }
    return { historyRecorded, countRecorded };
  }, [user?.uid]);

  const handlePrePrintConfirm = useCallback(async ({
    format,
    settings: modalSettings,
    copiesByOrder,
    directPrint = false,
    queueOnly = false,
    includeSeparators = false,
    qzSettings = {},
    giftCards = [],
  }) => {
    const toPrint = prePrintRequest?.orders || [];
    if (!toPrint.length || prePrintBusy) return;
    setPrePrintBusy(true);
    try {
      const settings = normalizePrintSettings({
        ...printSettings,
        ...modalSettings,
        copiesByOrder,
        margins: normalizePackingSlipMargins(modalSettings?.margins),
      });
      const enriched = await enrichOrdersWithOrderDeskCheckoutDates(toPrint, {
        filterDate: printCountDay || undefined,
        prefer: 'both',
      });
      const printCopiesByOrder = expandCopiesByOrderAliases(enriched, copiesByOrder);
      if (queueOnly) {
        printQueue.enqueuePackingSetup({
          orders: enriched,
          format,
          settings,
          copiesByOrder: printCopiesByOrder,
          qzSettings,
          giftCards,
          includeSeparators,
          source: 'printing-toolkit',
        });
        setOrderCopiesBySource((previous) => ({
          ...previous,
          ...copiesByOrder,
        }));
        setPrintBench(null);
        setPrePrintRequest(null);
        openQueueMode();
        return;
      }
      const resolvedFormat = coercePackingSlipFormat(format);
      const buildSlipHtml = (orders) =>
        resolvedFormat === PACKING_SLIP_FORMAT.CHEETAH
          ? buildCheetahLz17PackingSlipHTML(orders, {
              logoUrl: settings.logoUrl || DEFAULT_LOGO_URL,
              enrichAccounts: true,
              showPrices: settings.showPrices !== false,
              showSignatureLines: settings.showSignatureLines !== false,
            })
          : buildPrintableHTMLForOrders(
              orders,
              settings.logoUrl,
              settings,
            );
      const formatLabel =
        resolvedFormat === PACKING_SLIP_FORMAT.CHEETAH
          ? 'Cheetah LZ17'
          : resolvedFormat === PACKING_SLIP_FORMAT.GO4
            ? 'GO4'
            : 'Standard A4';
      const slipProfileKey =
        resolvedFormat === PACKING_SLIP_FORMAT.CHEETAH ? 'cheetah' : 'standard';
      const go4Options = {
        logoUrl: settings.logoUrl || DEFAULT_LOGO_URL,
        settings,
        showSignatureLines: settings.showSignatureLines !== false,
      };
      const physicalSheetsByOrder = {};
      const qzJobs = [];
      let separatorSheetCount = 0;
      const pushSlipAndMaybeGo4 = async (orders, labelPrefix = '') => {
        let slipHtml;
        let outsideHtml = '';
        if (resolvedFormat === PACKING_SLIP_FORMAT.GO4) {
          const parts = await buildGo4PrintDocuments(orders, go4Options);
          slipHtml = parts.slipHtml;
          outsideHtml = parts.outsideHtml;
        } else {
          slipHtml = await buildSlipHtml(orders);
        }
        const groupPages = countPhysicalPagesByOrderFromHtml(slipHtml);
        Object.entries(groupPages).forEach(([key, count]) => {
          physicalSheetsByOrder[key] = (physicalSheetsByOrder[key] || 0) + count;
        });
        const printableHtml = prepareHtmlForNativeBatchPrint(slipHtml, {
          copiesPerOrder: settings.copiesPerOrder,
          copiesByOrder: printCopiesByOrder,
        });
        const printableOutside = outsideHtml
          ? prepareHtmlForNativeBatchPrint(outsideHtml, {
              copiesPerOrder: settings.copiesPerOrder,
              copiesByOrder: printCopiesByOrder,
            })
          : '';
        if (directPrint) {
          qzJobs.push({
            profileKey: slipProfileKey,
            label: `${labelPrefix}${
              resolvedFormat === PACKING_SLIP_FORMAT.CHEETAH
                ? 'Cheetah labels'
                : 'Standard packing slips'
            }`.trim(),
            html: printableHtml,
            settings: qzSettings[slipProfileKey],
          });
          if (printableOutside) {
            qzJobs.push({
              profileKey: 'go4Outside',
              label: `${labelPrefix}GO4 outside labels`.trim(),
              html: printableOutside,
              settings: qzSettings.go4Outside,
            });
          }
        } else {
          await printHTMLSilent(printableHtml, { removeDelay: 1500 });
          if (printableOutside) {
            await printHTMLSilent(printableOutside, { removeDelay: 1500 });
          }
        }
        return groupPages;
      };
      if (directPrint && includeSeparators) {
        const groups = groupOrdersForPrintSeparators(enriched);
        for (const group of groups) {
          const groupPages = await pushSlipAndMaybeGo4(
            group.orders,
            `${group.shippingMethod} `,
          );
          const labelCount = countPrintGroupLabels(group.orders, printCopiesByOrder);
          const physicalSheetCount = Object.entries(groupPages).reduce(
            (total, [key, count]) =>
              total + count * Math.max(1, parseInt(printCopiesByOrder?.[key], 10) || 1),
            0,
          );
          qzJobs.push({
            profileKey: 'separator',
            label: `Start of ${group.shippingMethod}`,
            html: buildPrintSeparatorHtml(group, {
              outputLabel: `${group.shippingMethod} ${
                resolvedFormat === PACKING_SLIP_FORMAT.CHEETAH
                  ? 'labels'
                  : 'packing slips'
              }`,
              formatLabel,
              segmentLabel: group.shippingMethod,
              orderCount: group.orders.length,
              labelCount,
              physicalSheetCount,
              operator: user?.displayName || user?.email || user?.uid,
            }),
            settings: qzSettings.separator,
          });
          separatorSheetCount += 1;
        }
      } else {
        await pushSlipAndMaybeGo4(enriched);
      }
      if (giftCards.length) {
        if (directPrint && includeSeparators) {
          const giftGroups = groupItemsForPrintSeparators(giftCards);
          for (const group of giftGroups) {
            qzJobs.push({
              profileKey: 'giftCard',
              label: `A6 cards for ${group.shippingMethod}`,
              html: await buildEmbeddedGiftCardHtml(
                buildGiftCardPrintHtml,
                group.items,
              ),
              settings: withGiftCardTrayRotation(qzSettings.giftCard),
            });
            qzJobs.push({
              profileKey: 'separator',
              label: `Start of ${group.shippingMethod} (A6)`,
              html: buildPrintSeparatorHtml(group, {
                outputLabel: 'A6 inscription cards',
                formatLabel: 'A6 reverse landscape',
                segmentLabel: group.shippingMethod,
                orderCount: group.orders.length,
                labelCount: group.items.length,
                physicalSheetCount: group.items.length,
                operator: user?.displayName || user?.email || user?.uid,
              }),
              settings: qzSettings.separator,
            });
            separatorSheetCount += 1;
          }
        } else {
          const giftCardHtml = await buildEmbeddedGiftCardHtml(
            buildGiftCardPrintHtml,
            giftCards,
            directPrint ? undefined : { rotateForTray: true },
          );
          if (directPrint) {
            qzJobs.push({
              profileKey: 'giftCard',
              label: 'A6 inscription cards',
              html: giftCardHtml,
              settings: withGiftCardTrayRotation(qzSettings.giftCard),
            });
          } else {
            await printHTMLSilent(giftCardHtml, { removeDelay: 1500 });
          }
        }
      }
      setPendingPrintAudit({
        orders: enriched,
        settings: {
          ...settings,
          paperMode:
            resolvedFormat === PACKING_SLIP_FORMAT.CHEETAH
              ? 'cheetah-lz17'
              : resolvedFormat === PACKING_SLIP_FORMAT.GO4
                ? 'go4'
                : 'standard',
          printTransport: directPrint ? 'qz-tray' : 'browser',
          separatorSheetCount,
          giftCardCount: giftCards.length,
        },
        jobType:
          resolvedFormat === PACKING_SLIP_FORMAT.CHEETAH
            ? 'cheetah_lz17'
            : resolvedFormat === PACKING_SLIP_FORMAT.GO4
              ? 'go4'
              : 'packing_slip',
        physicalSheetsByOrder,
        copiesByOrder: printCopiesByOrder,
      });
      if (directPrint) {
        await printHtmlBatchWithQz(qzJobs);
      }
      setOrderCopiesBySource((previous) => ({
        ...previous,
        ...copiesByOrder,
      }));
      const benchLine = buildPrintBenchLine({
        format: resolvedFormat,
        slipSheetCount: countCopiedSheets(physicalSheetsByOrder, printCopiesByOrder),
        separatorSheetCount,
        giftCardCount: giftCards.length,
      });
      setPrintBench({
        line: benchLine,
        detail: directPrint
          ? 'Check the physical output before recording it as correct.'
          : 'The browser print dialog closed. Check the physical output before recording it as correct.',
      });
      setPrintJobStatus(null);
      setPrePrintRequest(null);
    } catch (error) {
      console.error('Pre-print job failed:', error);
      if (error?.submissionStarted) {
        setPrintBench(null);
        setPrintJobStatus({
          message: error.message,
          submitted: error.submitted || [],
          uncertain: error.uncertain || [],
          pending: error.pending || [],
        });
        setPrePrintRequest(null);
      } else {
        alert(`Failed before print submission: ${error.message || error}`);
      }
    } finally {
      setPrePrintBusy(false);
    }
  }, [
    prePrintRequest,
    prePrintBusy,
    printSettings,
    printCountDay,
    recordPrintJob,
    printQueue,
    openQueueMode,
    user?.displayName,
    user?.email,
    user?.uid,
  ]);

  // eslint-disable-next-line no-unused-vars
  const handleCheetahLz17Print = useCallback(async () => {
    const toPrint = selectedList.length ? selectedList : filteredOrders;
    if (!toPrint.length) {
      alert('No orders to print. Select some or adjust filters.');
      return;
    }
    const settings = normalizePrintSettings(printSettings);
    try {
      const enriched = await enrichOrdersWithOrderDeskCheckoutDates(toPrint, {
        filterDate: printCountDay || undefined,
        prefer: 'both',
      });
      const html = await buildCheetahLz17PackingSlipHTML(enriched, {
        logoUrl: settings.logoUrl || DEFAULT_LOGO_URL,
        enrichAccounts: true,
        showPrices: settings.showPrices !== false,
      });
      const physicalSheetsByOrder = countPhysicalPagesByOrderFromHtml(html);
      const copiesByOrder = buildCopiesByOrder(enriched, settings.copiesPerOrder);
      await printHtmlDocumentToPdf(html, {
        fileName: `cheetah-lz17-${new Date().toISOString().substring(0, 10)}.pdf`,
        orientation: 'p',
        pageFormat: 'a4',
        hostWidthPx: 794,
        copiesPerOrder: settings.copiesPerOrder,
        copiesByOrder,
      });
      await recordPrintJob(
        enriched,
        { ...settings, paperMode: 'cheetah-lz17' },
        'cheetah_lz17',
        physicalSheetsByOrder,
        copiesByOrder,
      );
    } catch (error) {
      console.error('Cheetah LZ17 print failed:', error);
      alert(`Failed to generate Cheetah LZ17 sheets: ${error.message || error}`);
    }
  }, [selectedList, filteredOrders, printSettings, recordPrintJob, printCountDay, buildCopiesByOrder]);

  // Enhanced print function with settings
  // eslint-disable-next-line no-unused-vars
  const handleEnhancedPrint = useCallback(async (ordersToPrint = null, customSettings = null) => {
    const toPrint = ordersToPrint || (selectedList.length ? selectedList : filteredOrders);
    if (!toPrint.length) {
      alert('No orders to print. Select some or adjust filters.');
      return;
    }

    const settings = normalizePrintSettings(customSettings || printSettings);
    
    try {
      // OrderDesk checkout_data.Dispatch / Delivery (Admin folder method) before slip HTML
      const enriched = await enrichOrdersWithOrderDeskCheckoutDates(toPrint, {
        filterDate: printCountDay || undefined,
        prefer: 'both',
      });

      const html = await buildPrintableHTMLForOrders(enriched, settings.logoUrl, settings);
      const physicalSheetsByOrder = countPhysicalPagesByOrderFromHtml(html);
      const copiesByOrder = buildCopiesByOrder(enriched, settings.copiesPerOrder);
      const settingsWithOrderCopies = { ...settings, copiesByOrder };
      await generatePdfFromHtml(html, settingsWithOrderCopies);
      await recordPrintJob(
        enriched,
        settingsWithOrderCopies,
        'packing_slip',
        physicalSheetsByOrder,
        copiesByOrder,
      );
      
    } catch (error) {
      console.error('Enhanced print failed:', error);
      alert('Failed to generate print job');
    }
  }, [selectedList, filteredOrders, printSettings, recordPrintJob, printCountDay, buildCopiesByOrder]);

  // Preview order
  const handlePreviewOrder = useCallback((order) => {
    setPreviewOrder(order);
    setShowPreviewModal(true);
  }, []);

  // Batch print with grouping
  const handleBatchPrint = useCallback(async () => {
    const toPrint = selectedList.length ? selectedList : filteredOrders;
    if (!toPrint.length) {
      alert('No orders selected for batch printing');
      return;
    }

    try {
      let groupedOrders = [...toPrint];
      
      // Apply grouping
      if (batchSettings.groupBy !== 'none') {
        groupedOrders = groupOrdersBy(toPrint, batchSettings.groupBy);
      }
      
      // Apply sorting
      groupedOrders = sortOrdersBy(groupedOrders, batchSettings.sortBy);
      
      openPrePrintDialog(groupedOrders);
      setShowBatchModal(false);
    } catch (error) {
      console.error('Batch print failed:', error);
      alert('Failed to complete batch print');
    }
  }, [selectedList, filteredOrders, batchSettings, openPrePrintDialog]);

  // Utility functions for batch processing
  const groupOrdersBy = (orders, groupBy) => {
    const groups = {};
    orders.forEach(order => {
      let key;
      switch (groupBy) {
        case 'shipping':
          key = order.shipping_method || 'No Method';
          break;
        case 'date':
          key = order.delivery_date || 'No Date';
          break;
        case 'company':
          key = order.companyName || order.customer?.company || 'No Company';
          break;
        default:
          key = 'all';
      }
      if (!groups[key]) groups[key] = [];
      groups[key].push(order);
    });
    return Object.values(groups).flat();
  };

  const sortOrdersBy = (orders, sortBy) => {
    return orders.sort((a, b) => {
      switch (sortBy) {
        case 'orderNumber':
          return (a.source_id || a.id || '').localeCompare(b.source_id || b.id || '');
        case 'date':
          return new Date(a.delivery_date || 0) - new Date(b.delivery_date || 0);
        case 'company':
          const aCompany = a.companyName || a.customer?.company || '';
          const bCompany = b.companyName || b.customer?.company || '';
          return aCompany.localeCompare(bCompany);
        default:
          return 0;
      }
    });
  };

  // Build printable HTML string that mirrors the Twig template
  const buildPrintableHTML = async (ordersToPrint, settingsOverride = null) => {
    const settings = normalizePrintSettings(settingsOverride || printSettings);
    const list = Array.isArray(ordersToPrint) ? ordersToPrint : [ordersToPrint];
    return buildPrintableHTMLForOrders(list, settings.logoUrl || logoUrl, settings);
  };

  // Internal utility: given full HTML (with style + multiple .main orders)
  // render each order block to PDF pages in sequence (no mixing orders on a page)
  // eslint-disable-next-line no-unused-vars
  const generatePdfFromHtmlLegacy = async (fullHtml) => {
    const { default: html2canvas } = await import('html2canvas');

    // Extract css and body
    let css = '';
    let body = fullHtml;
    try {
      const cssMatch = fullHtml.match(/<style>([\s\S]*?)<\/style>/i);
      if (cssMatch) css = cssMatch[1];
      const bodyMatch = fullHtml.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
      if (bodyMatch) body = bodyMatch[1];
    } catch {}

    // Parse into a container
    const host = document.createElement('div');
    host.style.position = 'fixed';
    host.style.left = '-99999px';
    host.style.top = '0';
    host.style.width = '794px';
    host.style.background = '#fff';
    host.innerHTML = `<style>${css}</style>${body}`;
    document.body.appendChild(host);

    // Find per-order blocks
    const blocks = Array.from(host.querySelectorAll('.main'));
    const pdf = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    for (let i = 0; i < blocks.length; i++) {
      const block = blocks[i];
      // Create isolated container per order to avoid cross-order pagination
      const container = document.createElement('div');
      container.style.position = 'fixed';
      container.style.left = '-99999px';
      container.style.top = '0';
      container.style.width = '794px';
      container.style.background = '#fff';
      container.innerHTML = `<style>${css}</style>${block.outerHTML}`;
      document.body.appendChild(container);

      // Ensure images load
      const imgs = Array.from(container.querySelectorAll('img'));
      await Promise.all(
        imgs.map((img) => new Promise((resolve) => {
          if (img.complete) return resolve();
          img.onload = img.onerror = () => resolve();
        }))
      );

      const canvas = await html2canvas(container, {
        useCORS: true,
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        windowWidth: 794,
      });

      const imgData = canvas.toDataURL('image/png');
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      if (i > 0) pdf.addPage(); // start a new page for each order

      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
      heightLeft -= pdfHeight;
      while (heightLeft > 0) {
        position -= pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
        heightLeft -= pdfHeight;
      }

      document.body.removeChild(container);
    }

    document.body.removeChild(host);
    pdf.save(`packing-slips-${new Date().toISOString().substring(0,10)}.pdf`);
  };

  // Public handler in this UI
  const handlePrint = async () => {
    const toPrint = selectedList.length ? selectedList : filteredOrders;
    if (!toPrint.length) {
      alert('No orders to print. Select some or adjust filters.');
      return;
    }
    let copies = 1;
    const ans = window.prompt('How many copies per order?', '1');
    if (ans && !isNaN(Number(ans))) copies = Math.max(1, parseInt(ans, 10) || 1);
    try {
      const enriched = await enrichOrdersWithOrderDeskCheckoutDates(toPrint, {
        filterDate: printCountDay || undefined,
        prefer: 'both',
      });
      const html = await buildPrintableHTML(enriched);
      await generatePdfFromHtml(html, { ...normalizePrintSettings(printSettings), copiesPerOrder: copies });
    } catch (e) {
      console.error('[PrintingToolKit] print failed:', e);
      alert('Failed to generate PDF.');
    }
  };

  

  return (
    <div className="toolkit-root printing-toolkit lsc-glass-scope">
      <div className="printing-header toolkit-header">
        <div className="printing-header__lead">
          <h1 className="toolkit-title">Printing Toolkit</h1>
          <p className="printing-header__sub">
            Pack this folder, then Other paper or Queue. Filters and the mixed line stay put.
          </p>
        </div>
        <div className="header-actions printing-setup">
          <button
            type="button"
            className="toolkit-btn toolkit-btn--ghost toolkit-btn--compact"
            aria-expanded={showSetupMenu}
            aria-haspopup="true"
            onClick={() => setShowSetupMenu((open) => !open)}
          >
            <SettingOutlined /> Setup
          </button>
          {showSetupMenu ? (
            <div className="printing-setup__menu" role="menu">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setShowSettingsModal(true);
                  setShowSetupMenu(false);
                }}
              >
                Settings
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setShowTemplateModal(true);
                  setShowSetupMenu(false);
                }}
              >
                Templates
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setShowHistoryModal(true);
                  setShowSetupMenu(false);
                }}
              >
                History
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {printBench ? (
        <div className="print-bench-strip" role="status">
          <div>
            <b>{printBench.line}</b>
            <span>{printBench.detail}</span>
          </div>
          <div className="print-bench-strip__actions">
            {pendingPrintAudit ? (
              <button
                type="button"
                className="toolkit-btn toolkit-btn--secondary toolkit-btn--compact"
                disabled={prePrintBusy}
                onClick={async () => {
                  setPrePrintBusy(true);
                  const auditResult = await recordPrintJob(
                    pendingPrintAudit.orders,
                    { ...pendingPrintAudit.settings, outputConfirmed: true },
                    pendingPrintAudit.jobType,
                    pendingPrintAudit.physicalSheetsByOrder,
                    pendingPrintAudit.copiesByOrder,
                  );
                  setPendingPrintAudit(null);
                  setPrintBench(
                    auditResult.historyRecorded && auditResult.countRecorded
                      ? null
                      : {
                          line: printBench.line,
                          detail:
                            'Output was confirmed, but print history could not be fully saved. Inform an administrator before relying on the Printed count.',
                        },
                  );
                  setPrePrintBusy(false);
                }}
              >
                Output checked
              </button>
            ) : null}
            <button
              type="button"
              className="toolkit-btn toolkit-btn--ghost toolkit-btn--compact"
              onClick={() => setPrintBench(null)}
            >
              Dismiss
            </button>
          </div>
        </div>
      ) : null}

      <div className="printing-toolbar">
        <div className="printing-tabs printing-modes" role="tablist" aria-label="Printing Toolkit modes">
          <button
            type="button"
            role="tab"
            aria-selected={toolkitMode === PRINTING_TOOLKIT_MODES.PACK}
            className={`tab-pill ${toolkitMode === PRINTING_TOOLKIT_MODES.PACK ? 'active' : ''}`}
            onClick={() => setToolkitMode(PRINTING_TOOLKIT_MODES.PACK)}
          >
            Pack
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={toolkitMode === PRINTING_TOOLKIT_MODES.OTHER}
            className={`tab-pill ${toolkitMode === PRINTING_TOOLKIT_MODES.OTHER ? 'active' : ''}`}
            onClick={() => setToolkitMode(PRINTING_TOOLKIT_MODES.OTHER)}
          >
            Other paper
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={toolkitMode === PRINTING_TOOLKIT_MODES.QUEUE}
            className={`tab-pill ${toolkitMode === PRINTING_TOOLKIT_MODES.QUEUE ? 'active' : ''}`}
            onClick={openQueueMode}
          >
            Queue
          </button>
        </div>
        {toolkitMode === PRINTING_TOOLKIT_MODES.OTHER ? (
          <div className="printing-tabs printing-order-tabs" role="tablist" aria-label="Other paper">
            <button
              type="button"
              role="tab"
              aria-selected={otherTab === 'labels'}
              className={`tab-pill tab-pill--secondary ${otherTab === 'labels' ? 'active' : ''}`}
              onClick={() => setOtherTab('labels')}
            >
              Labels
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={otherTab === 'inscriptions'}
              className={`tab-pill tab-pill--secondary ${otherTab === 'inscriptions' ? 'active' : ''}`}
              onClick={() => setOtherTab('inscriptions')}
            >
              Inscriptions
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={otherTab === 'general'}
              className={`tab-pill tab-pill--secondary ${otherTab === 'general' ? 'active' : ''}`}
              onClick={() => setOtherTab('general')}
            >
              General
            </button>
          </div>
        ) : null}
      </div>

      {toolkitMode === PRINTING_TOOLKIT_MODES.OTHER && otherTab === 'general' && (
        <GeneralPrintPanel
          sizeKey={generalSizeKey}
          copies={generalCopies}
          pageRanges={generalPageRanges}
          notes={generalNotes}
          onSessionPatch={patchGeneralSession}
          onOpenQueue={openQueueMode}
        />
      )}

      {toolkitMode === PRINTING_TOOLKIT_MODES.QUEUE && (
        <PrintQueuePanel />
      )}

      {toolkitMode === PRINTING_TOOLKIT_MODES.PACK && (
        <>
          <div className="printing-filters filters printing-filters--station">
            <div>
              <label htmlFor="ptk-filter-folder">Folder</label>
              <select
                id="ptk-filter-folder"
                value={selectedFolderId}
                onChange={(e) => setSelectedFolderId(e.target.value)}
                disabled={foldersLoading || Array.isArray(incomingOrders)}
              >
                <option value="">
                  {foldersLoading ? 'Loading folders…' : 'Select folder'}
                </option>
                {availableFolders.map((folder) => (
                  <option key={String(folder.id)} value={String(folder.id)}>
                    {folder.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="printing-order-lookup">
              <label htmlFor="ptk-filter-order">Find &amp; print</label>
              <div className="printing-order-lookup__row">
                <input
                  ref={findInputRef}
                  id="ptk-filter-order"
                  type="text"
                  placeholder="Order # or source ID  ·  / to focus"
                  value={searchId}
                  onChange={(e) => {
                    setSearchId(e.target.value);
                    if (orderLookupStatus) setOrderLookupStatus('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      findAndPrintOrderByIdentifier();
                    }
                  }}
                  disabled={orderLookupBusy}
                />
                <button
                  type="button"
                  className="toolkit-btn toolkit-btn--secondary"
                  onClick={findAndPrintOrderByIdentifier}
                  disabled={orderLookupBusy || !String(searchId || '').trim()}
                >
                  <SearchOutlined />{' '}
                  {orderLookupBusy ? 'Looking…' : 'Find & print'}
                </button>
              </div>
              {orderLookupStatus ? (
                <p className="printing-order-lookup__status">{orderLookupStatus}</p>
              ) : null}
            </div>
            <div className="printing-filter-tools">
              <button
                type="button"
                className={`toolkit-btn toolkit-btn--compact${
                  showNarrowFilters ? ' toolkit-btn--secondary' : ' toolkit-btn--ghost'
                }`}
                onClick={() => setShowNarrowFilters((open) => !open)}
              >
                Narrow this folder
              </button>
              <button
                type="button"
                className="toolkit-btn toolkit-btn--ghost toolkit-btn--compact"
                onClick={loadFolderOrders}
                disabled={!selectedFolder || isLoading || Array.isArray(incomingOrders)}
              >
                Refresh
              </button>
            </div>
            {showNarrowFilters ? (
              <>
                <div>
                  <label htmlFor="ptk-filter-date-field">Folder date narrow</label>
                  <div className="printing-date-field-toggle" role="group" aria-label="Folder date narrow">
                    <button
                      type="button"
                      id="ptk-filter-date-field"
                      className={`toolkit-btn toolkit-btn--compact${
                        dateFilterField === 'all' ? ' toolkit-btn--secondary' : ' toolkit-btn--ghost'
                      }`}
                      onClick={() => setDateFilterField('all')}
                      disabled={!selectedFolder || Array.isArray(incomingOrders)}
                      title="Everyone OrderDesk has in this folder (matches the purple list)"
                    >
                      All in folder
                    </button>
                    <button
                      type="button"
                      className={`toolkit-btn toolkit-btn--compact${
                        dateFilterField === 'delivery' ? ' toolkit-btn--secondary' : ' toolkit-btn--ghost'
                      }`}
                      onClick={() => setDateFilterField('delivery')}
                      disabled={
                        !selectedFolder ||
                        Array.isArray(incomingOrders) ||
                        !isOrderDeskDateFolderName(selectedFolder?.name)
                      }
                      title="Only orders whose Delivery equals this folder date"
                    >
                      Delivery day
                    </button>
                    <button
                      type="button"
                      className={`toolkit-btn toolkit-btn--compact${
                        dateFilterField === 'dispatch' ? ' toolkit-btn--secondary' : ' toolkit-btn--ghost'
                      }`}
                      onClick={() => setDateFilterField('dispatch')}
                      disabled={
                        !selectedFolder ||
                        Array.isArray(incomingOrders) ||
                        !isOrderDeskDateFolderName(selectedFolder?.name)
                      }
                      title="Only orders whose Dispatch equals this folder date"
                    >
                      Dispatch day
                    </button>
                  </div>
                </div>
                <div>
                  <label htmlFor="ptk-filter-shipping">Shipping method</label>
                  <select
                    id="ptk-filter-shipping"
                    value={filterShipping}
                    onChange={(e) => setFilterShipping(e.target.value)}
                  >
                    <option value="">All methods</option>
                    {shippingMethodOptions.map((method) => (
                      <option key={method} value={method}>
                        {method}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="ptk-filter-company">Company</label>
                  <select
                    id="ptk-filter-company"
                    value={filterCompany}
                    onChange={(e) => setFilterCompany(e.target.value)}
                  >
                    <option value="">All companies</option>
                    {companyOptions.map((company) => (
                      <option key={company} value={company}>
                        {company}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            ) : null}
          </div>

          <div className="print-actions printing-command-bar">
            <div className="action-buttons">
              <button
                type="button"
                className="toolkit-btn toolkit-btn--primary"
                disabled={!selectedList.length && !filteredOrders.length}
                onClick={() => waveDeskRef.current?.assembleDayStack?.()}
              >
                <PrinterOutlined /> {WAVE_COMMAND_LABELS.assembleDay}
              </button>
              <button
                type="button"
                className="toolkit-btn toolkit-btn--secondary"
                disabled={!selectedList.length && !filteredOrders.length}
                onClick={() => waveDeskRef.current?.assembleAndStart?.()}
              >
                {WAVE_COMMAND_LABELS.assembleAndStart}
              </button>
              <button
                type="button"
                className="toolkit-btn toolkit-btn--ghost"
                disabled={!selectedList.length && !filteredOrders.length}
                onClick={() => waveDeskRef.current?.printWave?.()}
              >
                {WAVE_COMMAND_LABELS.printWave}
              </button>
              <button
                type="button"
                className="toolkit-btn toolkit-btn--ghost"
                disabled={!selectedList.length && !filteredOrders.length}
                onClick={() => waveDeskRef.current?.printPickList?.()}
              >
                <UnorderedListOutlined /> {WAVE_COMMAND_LABELS.printPickList}
              </button>
              <button
                type="button"
                className="toolkit-btn toolkit-btn--ghost"
                disabled={!selectedList.length && !filteredOrders.length}
                onClick={() => waveDeskRef.current?.queuePickList?.()}
              >
                {WAVE_COMMAND_LABELS.queuePickList}
              </button>
              <button
                type="button"
                className="toolkit-btn toolkit-btn--secondary"
                onClick={() => openPrePrintDialog()}
              >
                Print {selectedList.length ? `${selectedList.length} selected` : `${filteredOrders.length} filtered`}
              </button>
              <button
                type="button"
                className="toolkit-btn toolkit-btn--ghost"
                onClick={() => toggleAll(true)}
                disabled={!filteredOrders.length}
              >
                Select all
              </button>
              <button
                type="button"
                className="toolkit-btn toolkit-btn--ghost"
                onClick={() => toggleAll(false)}
                disabled={!Object.keys(selectedIds).length}
              >
                Clear selection
              </button>
            </div>
            <div className="print-info">
              <span className="info-text">
                {isLoading || foldersLoading
                  ? 'Loading orders…'
                  : (() => {
                      const packedCount = filteredOrders.filter((o) => isOrderPacked(o)).length;
                      const shippedCount = filteredOrders.filter((o) => isOrderShipped(o)).length;
                      const packShipBits = [
                        packedCount ? `${packedCount} packed` : null,
                        shippedCount ? `${shippedCount} shipped` : null,
                      ].filter(Boolean);
                      return `${filteredOrders.length} shown${
                        selectedList.length ? `, ${selectedList.length} selected` : ''
                      }${packShipBits.length ? ` · ${packShipBits.join(', ')}` : ''}${
                        orderLoadStatus ? ` · ${orderLoadStatus}` : ''
                      } · Enter prints selection · / Find`;
                    })()}
              </span>
            </div>
          </div>

          <div ref={listRef} className="printing-orders-card toolkit-card">
            {isLoading && <p className="printing-empty">Loading…</p>}
            {!isLoading && filteredOrders.length === 0 && (
              <p className="printing-empty">
                {String(searchId || '').trim()
                  ? 'No loaded orders match that ID. Use Find & print to look up OrderDesk and open print setup.'
                  : 'No orders match your filters.'}
              </p>
            )}
            {!isLoading && filteredOrders.length > 0 && (
              <table className="printing-orders-table">
                <thead>
                  <tr>
                    <th>Select</th>
                    {ORDERS_TABLE_SORTABLE.map(({ key, label }) => {
                      const active = ordersSort.key === key;
                      const ariaSort = active
                        ? ordersSort.dir === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none';
                      return (
                        <th
                          key={key}
                          className={`is-sortable${active ? ` is-sorted is-sorted-${ordersSort.dir}` : ''}`}
                          onClick={() => handleOrdersSort(key)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleOrdersSort(key);
                            }
                          }}
                          role="columnheader"
                          tabIndex={0}
                          aria-sort={ariaSort}
                          title={`Sort by ${label}`}
                        >
                          {label}
                          <span className="ptk-sort-indicator" aria-hidden="true">
                            {active ? (ordersSort.dir === 'asc' ? '↑' : '↓') : '↕'}
                          </span>
                        </th>
                      );
                    })}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFilteredOrders.map((o) => {
                      const items = Array.isArray(o?.order_items) ? o.order_items : [];
                      const qtyTotal = getQuantityTotal(o);
                      const dup = shouldPrintDuplicate(o);
                      const delivery = formatPrintingDeliveryDisplay(o);
                      const dispatch = formatPrintingDispatchDisplay(o);
                      const printedCount = lookupPackingSlipPrintCount(printCountMap, o);
                      const orderNumber = getOrderPrintSourceId(o) || o.id;
                      const orderCopies = Math.max(
                        1,
                        parseInt(orderCopiesBySource[orderNumber], 10) ||
                          printSettings.copiesPerOrder ||
                          1,
                      );
                      const packed = isOrderPacked(o);
                      const shipped = isOrderShipped(o);
                      const isPending = isWholesaleUnconfirmedOrder(o);
                      const isSentToXero = Boolean(o?.sentToXero || o?.linkedFromXeroFolder);
                      const rowClass = [
                        selectedIds[o.id] ? 'is-selected' : '',
                        shipped ? 'is-shipped' : '',
                        packed && !shipped ? 'is-packed' : '',
                        isPending ? 'is-pending-wholesale' : '',
                        isSentToXero ? 'is-sent-to-xero' : '',
                      ]
                        .filter(Boolean)
                        .join(' ');
                      return (
                        <tr key={o.id} className={rowClass}>
                          <td>
                            <input
                              type="checkbox"
                              checked={!!selectedIds[o.id]}
                              onChange={() => toggleOne(o.id)}
                              aria-label={`Select order ${o.source_id || o.id}`}
                            />
                          </td>
                          <td>
                            <div className="ptk-order-copy-control">
                              <strong>{o.source_id || o.id}</strong>
                              {(() => {
                                const ship = resolveShipLabelOrderStatus(o, shipLabelStatusMap);
                                return (
                                  <ShipLabelStatusChip
                                    status={ship.status}
                                    pageCount={ship.pageCount}
                                    onOpenDesk={openWaveDeskForShipLabels}
                                  />
                                );
                              })()}
                              <label>
                                <span>Labels</span>
                                <input
                                  type="number"
                                  min="1"
                                  max="99"
                                  step="1"
                                  inputMode="numeric"
                                  value={orderCopies}
                                  onChange={(event) => {
                                    const next = Math.max(
                                      1,
                                      Math.min(99, parseInt(event.target.value, 10) || 1),
                                    );
                                    setOrderCopiesBySource((previous) => ({
                                      ...previous,
                                      [orderNumber]: next,
                                    }));
                                  }}
                                  aria-label={`Labels for order ${orderNumber}`}
                                  title="Number of packing-slip label copies for this order"
                                />
                              </label>
                            </div>
                          </td>
                          <td>
                            <div className="ptk-pack-status" aria-label="Pack / ship status">
                              {shipped ? (
                                <span className="ptk-pack-badge ptk-pack-badge--shipped" title="Marked shipped in Packers">
                                  Shipped
                                </span>
                              ) : null}
                              {packed ? (
                                <span className="ptk-pack-badge ptk-pack-badge--packed" title="Marked packed in Packers">
                                  Packed
                                </span>
                              ) : null}
                              {isSentToXero ? (
                                <span
                                  className="ptk-pack-badge ptk-pack-badge--xero"
                                  title={`Linked from ${XERO_WHOLESALE_FOLDER_NAME} for this folder date window`}
                                >
                                  Sent to Xero
                                </span>
                              ) : null}
                              {isPending ? (
                                <span
                                  className="ptk-pack-badge ptk-pack-badge--pending"
                                  title="Still in Wholesale landing — not yet confirmed for packing"
                                >
                                  Pending
                                </span>
                              ) : null}
                              {!packed && !shipped && !isSentToXero && !isPending ? (
                                <span className="ptk-pack-badge ptk-pack-badge--open" title="Not packed or shipped yet">
                                  Open
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td>
                            <span
                              className={`ptk-print-count${printedCount > 0 ? ' ptk-print-count--done' : ''}`}
                              title={
                                printedCount > 0
                                  ? `Printed in ${printedCount} run${printedCount === 1 ? '' : 's'} for this delivery day`
                                  : 'Not printed yet for this delivery day'
                              }
                            >
                              {printedCount}
                            </span>
                          </td>
                          <td>{sanitize(getPrintingOrderCompany(o))}</td>
                          <td>{dispatch}</td>
                          <td>{delivery}</td>
                          <td>{getPrintingOrderShippingMethod(o)}</td>
                          <td>{items.length}</td>
                          <td>{qtyTotal}</td>
                          <td>{dup ? 'Yes' : 'No'}</td>
                          <td>
                            <div className="row-actions">
                              <button
                                type="button"
                                className="toolkit-btn toolkit-btn--ghost toolkit-btn--compact"
                                onClick={() => handlePreviewOrder(o)}
                                title="Preview"
                              >
                                <EyeOutlined />
                              </button>
                              <button
                                type="button"
                                className="toolkit-btn toolkit-btn--primary toolkit-btn--compact ptk-row-print"
                                onClick={() => setOrderPartsTarget(o)}
                                title={
                                  printedCount > 0
                                    ? `Reprint · already printed ${printedCount} time${printedCount === 1 ? '' : 's'} this delivery day`
                                    : 'Print this order — choose parts'
                                }
                              >
                                <PrinterOutlined /> {printedCount > 0 ? 'Reprint' : 'Print'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            )}
          </div>
          <div className="toolkit-card wave-desk-card" ref={waveDeskCardRef}>
              <WaveDeskPanel
              ref={waveDeskRef}
              showCommandBar={false}
              orders={selectedList.length ? selectedList : filteredOrders}
              folderLabel={selectedFolder?.name || printCountDay || ''}
              folderId={selectedFolderId || ''}
              workingDeliveryDate={printCountDay}
              isPacked={isOrderPacked}
              isShipped={isOrderShipped}
              teamAttaches={teamAttaches}
              onOpenPrePrint={openPrePrintDialog}
              onOpenQueue={openQueueMode}
            />
          </div>
          {attachReview ? (
            <div className="toolkit-card">
              <ShippingLabelAttachReview
                open
                blob={attachReview.blob}
                fileName={attachReview.fileName}
                driveItemId={attachReview.driveItemId}
                orders={selectedList.length ? selectedList : filteredOrders}
                workingDeliveryDate={printCountDay}
                folderId={selectedFolderId || ''}
                folderLabel={selectedFolder?.name || ''}
                user={user}
                onClose={() => setAttachReview(null)}
                onSaved={() => {
                  setAttachReview(null);
                }}
              />
            </div>
          ) : null}
          <div className="toolkit-card">
            <PrintRunMatrixPanel
              orders={selectedList.length ? selectedList : filteredOrders}
              attaches={teamAttaches}
              folderLabel={selectedFolder?.name || printCountDay || ''}
              workingDeliveryDate={printCountDay}
              plannerViews={plannerViews}
              onOpenQueue={openQueueMode}
            />
          </div>
          <OrderPrintPartsDialog
            open={Boolean(orderPartsTarget)}
            order={orderPartsTarget}
            attaches={teamAttaches}
            workingDeliveryDate={printCountDay}
            folderLabel={selectedFolder?.name || printCountDay || ''}
            onClose={() => setOrderPartsTarget(null)}
            onOpenQueue={openQueueMode}
          />
        </>
      )}

      {toolkitMode === PRINTING_TOOLKIT_MODES.OTHER && otherTab === 'labels' && (
        <ShippingLabelPdfPanel orders={orders} workingDeliveryDate={printCountDay} />
      )}

      {toolkitMode === PRINTING_TOOLKIT_MODES.OTHER && otherTab === 'inscriptions' && (
        <GiftInscriptionPanel
          orders={filteredOrders}
          lookupOrder={lookupPrintingInscriptionOrder}
          title="A6 gift inscription cards"
          description="Review possible gift messages from the currently filtered orders, or look up any order number / source ID. If it is not in the loaded OrderDesk folder list, OrderDesk notes and gift fields are fetched automatically."
        />
      )}

      <PackingPrePrintDialog
        open={Boolean(prePrintRequest)}
        orders={prePrintRequest?.orders || []}
        initialFormat={prePrintRequest?.initialFormat || 'standard'}
        initialSettings={prePrintRequest?.settings || printSettings}
        initialCopiesByOrder={prePrintInitialCopies}
        defaultsNote={prePrintRequest?.defaultsNote || ''}
        busy={prePrintBusy}
        onClose={() => {
          if (!prePrintBusy) setPrePrintRequest(null);
        }}
        onConfirm={handlePrePrintConfirm}
      />
      <PrintJobStatusDialog
        status={printJobStatus}
        busy={prePrintBusy}
        onClose={() => setPrintJobStatus(null)}
        onConfirmOutput={
          pendingPrintAudit
            ? async () => {
                setPrePrintBusy(true);
                const auditResult = await recordPrintJob(
                  pendingPrintAudit.orders,
                  { ...pendingPrintAudit.settings, outputConfirmed: true },
                  pendingPrintAudit.jobType,
                  pendingPrintAudit.physicalSheetsByOrder,
                  pendingPrintAudit.copiesByOrder,
                );
                setPendingPrintAudit(null);
                setPrintJobStatus(
                  auditResult.historyRecorded && auditResult.countRecorded
                    ? null
                    : {
                        message:
                          'Output was confirmed, but print history could not be fully saved. Inform an administrator before relying on the Printed count.',
                        submitted: [{ label: 'Physical output confirmed' }],
                      },
                );
                setPrePrintBusy(false);
              }
            : undefined
        }
        onRetryUnconfirmed={
          printJobStatus?.uncertain?.length || printJobStatus?.pending?.length
            ? async () => {
                setPrePrintBusy(true);
                try {
                  const jobs = [
                    ...(printJobStatus.uncertain || []),
                    ...(printJobStatus.pending || []),
                  ];
                  const submitted = await printHtmlBatchWithQz(jobs);
                  setPrintJobStatus({
                    submitted,
                    message:
                      'The selected unconfirmed output was submitted to QZ. Check it before recording the run.',
                  });
                } catch (error) {
                  setPrintJobStatus({
                    message: error.message,
                    submitted: error.submitted || [],
                    uncertain: error.uncertain || [],
                    pending: error.pending || [],
                  });
                } finally {
                  setPrePrintBusy(false);
                }
              }
            : undefined
        }
        onBrowserFallback={
          printJobStatus?.uncertain?.length || printJobStatus?.pending?.length
            ? async () => {
                setPrePrintBusy(true);
                try {
                  const jobs = [
                    ...(printJobStatus.uncertain || []),
                    ...(printJobStatus.pending || []),
                  ];
                  for (const job of jobs) {
                    await printHTMLSilent(job.html, { removeDelay: 1500 });
                  }
                  setPrintJobStatus({
                    submitted: jobs,
                    message:
                      'Browser dialogs closed for the selected unconfirmed output. Check it before recording the run.',
                  });
                } catch (error) {
                  setPrintJobStatus({ message: error.message, uncertain: [] });
                } finally {
                  setPrePrintBusy(false);
                }
              }
            : undefined
        }
      />

      {showSettingsModal && (
        <PrintSettingsModal
          settings={printSettings}
          onSave={savePrintSettings}
          onClose={() => setShowSettingsModal(false)}
          savedSettings={savedSettings}
          onLoadSettings={loadSavedSettings}
        />
      )}

      {showHistoryModal && (
        <PrintHistoryModal
          history={printHistory}
          onClose={() => setShowHistoryModal(false)}
        />
      )}

      {showPreviewModal && previewOrder && (
        <PrintPreviewModal
          order={previewOrder}
          settings={{
            ...printSettings,
            copiesPerOrder:
              orderCopiesBySource[getOrderPrintSourceId(previewOrder)] ||
              printSettings.copiesPerOrder,
          }}
          logoUrl={logoUrl}
          filterDate={printCountDay}
          shipLabelAttaches={deskShipLabelAttaches}
          onClose={() => setShowPreviewModal(false)}
          onPrint={() => {
            setShowPreviewModal(false);
            openPrePrintDialog([previewOrder]);
          }}
        />
      )}

      {showBatchModal && (
        <BatchPrintModal
          settings={batchSettings}
          onSettingsChange={setBatchSettings}
          onPrint={handleBatchPrint}
          onClose={() => setShowBatchModal(false)}
          orderCount={selectedList.length || filteredOrders.length}
        />
      )}
    </div>
  );
};

// ─── Modal Components ───────────────────────────────────────────────────────

// Print Settings Modal
function PrintSettingsModal({ settings, onSave, onClose, savedSettings, onLoadSettings }) {
  const [formSettings, setFormSettings] = useState(normalizePrintSettings(settings));
  const [settingsName, setSettingsName] = useState('');

  const handleSave = () => {
    onSave(formSettings);
    onClose();
  };

  const handleSaveAs = () => {
    if (!settingsName.trim()) {
      alert('Please enter a name for the settings');
      return;
    }
    onSave(formSettings, settingsName);
    setSettingsName('');
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content large">
        <div className="modal-header">
          <h2>Print Settings</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          <div className="settings-tabs">
            <div className="settings-section">
              <h3>Paper & Layout</h3>
              <div className="form-grid">
                <div className="form-field">
                  <label>Paper Size</label>
                  <select 
                    value={formSettings.paperSize}
                    onChange={(e) => setFormSettings(prev => ({ ...prev, paperSize: e.target.value }))}
                  >
                    <option value="A4">A4</option>
                    <option value="A5">A5</option>
                    <option value="A6">A6</option>
                    <option value="Letter">Letter</option>
                    <option value="label">Label</option>
                    <option value="custom">Custom (mm)</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>Orientation</label>
                  <select 
                    value={formSettings.orientation}
                    onChange={(e) => setFormSettings(prev => ({ ...prev, orientation: e.target.value }))}
                  >
                    <option value="portrait">Portrait</option>
                    <option value="landscape">Landscape</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>Copies per Order</label>
                  <input 
                    type="number" 
                    min="1" 
                    max="10"
                    value={formSettings.copiesPerOrder}
                    onChange={(e) => setFormSettings(prev => ({ ...prev, copiesPerOrder: parseInt(e.target.value) || 1 }))}
                  />
                </div>
                <div className="form-field">
                  <label>Scale (%)</label>
                  <input
                    type="number"
                    min="10"
                    max="200"
                    value={formSettings.scalePercent}
                    onChange={(e) => setFormSettings(prev => ({ ...prev, scalePercent: Number(e.target.value) || 100 }))}
                  />
                </div>
              </div>

              {formSettings.paperSize === 'label' && (
                <div className="form-grid" style={{ marginTop: '0.75rem' }}>
                  <div className="form-field">
                    <label>Label Preset</label>
                    <select
                      value={formSettings.labelPreset}
                      onChange={(e) => {
                        const presetKey = e.target.value;
                        const preset = LABEL_PRESETS_MM[presetKey];
                        setFormSettings(prev => ({
                          ...prev,
                          labelPreset: presetKey,
                          labelMm: preset ? { ...preset } : prev.labelMm
                        }));
                      }}
                    >
                      {Object.keys(LABEL_PRESETS_MM).map((key) => (
                        <option key={key} value={key}>{key} mm</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Label Width (mm)</label>
                    <input
                      type="number"
                      min="20"
                      value={formSettings.labelMm?.width ?? 100}
                      onChange={(e) => setFormSettings(prev => ({ ...prev, labelMm: { ...(prev.labelMm || {}), width: Number(e.target.value) } }))}
                    />
                  </div>
                  <div className="form-field">
                    <label>Label Height (mm)</label>
                    <input
                      type="number"
                      min="20"
                      value={formSettings.labelMm?.height ?? 150}
                      onChange={(e) => setFormSettings(prev => ({ ...prev, labelMm: { ...(prev.labelMm || {}), height: Number(e.target.value) } }))}
                    />
                  </div>
                </div>
              )}

              {formSettings.paperSize === 'custom' && (
                <div className="form-grid" style={{ marginTop: '0.75rem' }}>
                  <div className="form-field">
                    <label>Page Width (mm)</label>
                    <input
                      type="number"
                      min="20"
                      value={formSettings.customPaperMm?.width ?? 210}
                      onChange={(e) => setFormSettings(prev => ({ ...prev, customPaperMm: { ...(prev.customPaperMm || {}), width: Number(e.target.value) } }))}
                    />
                  </div>
                  <div className="form-field">
                    <label>Page Height (mm)</label>
                    <input
                      type="number"
                      min="20"
                      value={formSettings.customPaperMm?.height ?? 297}
                      onChange={(e) => setFormSettings(prev => ({ ...prev, customPaperMm: { ...(prev.customPaperMm || {}), height: Number(e.target.value) } }))}
                    />
                  </div>
                </div>
              )}

              <div className="form-grid" style={{ marginTop: '0.75rem' }}>
                <div className="form-field">
                  <label>Margin Top (mm)</label>
                  <input
                    type="number"
                    min="0"
                    value={formSettings.margins?.top ?? 15}
                    onChange={(e) => setFormSettings(prev => ({ ...prev, margins: { ...(prev.margins || createStandardPackingSlipMargins()), top: Number(e.target.value) } }))}
                  />
                </div>
                <div className="form-field">
                  <label>Margin Right (mm)</label>
                  <input
                    type="number"
                    min="0"
                    value={formSettings.margins?.right ?? 15}
                    onChange={(e) => setFormSettings(prev => ({ ...prev, margins: { ...(prev.margins || createStandardPackingSlipMargins()), right: Number(e.target.value) } }))}
                  />
                </div>
                <div className="form-field">
                  <label>Margin Bottom (mm)</label>
                  <input
                    type="number"
                    min="0"
                    value={formSettings.margins?.bottom ?? 15}
                    onChange={(e) => setFormSettings(prev => ({ ...prev, margins: { ...(prev.margins || createStandardPackingSlipMargins()), bottom: Number(e.target.value) } }))}
                  />
                </div>
                <div className="form-field">
                  <label>Margin Left (mm)</label>
                  <input
                    type="number"
                    min="0"
                    value={formSettings.margins?.left ?? 15}
                    onChange={(e) => setFormSettings(prev => ({ ...prev, margins: { ...(prev.margins || createStandardPackingSlipMargins()), left: Number(e.target.value) } }))}
                  />
                </div>
              </div>
            </div>

            <div className="settings-section">
              <h3>Content Options</h3>
              <div className="form-grid">
                <div className="form-field checkbox">
                  <label>
                    <input 
                      type="checkbox"
                      checked={formSettings.showPrices}
                      onChange={(e) => setFormSettings(prev => ({ ...prev, showPrices: e.target.checked }))}
                    />
                    Show Prices
                  </label>
                </div>
                <div className="form-field checkbox">
                  <label>
                    <input 
                      type="checkbox"
                      checked={formSettings.showKgLines}
                      onChange={(e) => setFormSettings(prev => ({ ...prev, showKgLines: e.target.checked }))}
                    />
                    Show KG Lines
                  </label>
                </div>
                <div className="form-field checkbox">
                  <label>
                    <input 
                      type="checkbox"
                      checked={formSettings.includeDuplicate}
                      onChange={(e) => setFormSettings(prev => ({ ...prev, includeDuplicate: e.target.checked }))}
                    />
                    Include Duplicate Copy
                  </label>
                </div>
                <div className="form-field checkbox">
                  <label>
                    <input 
                      type="checkbox"
                      checked={formSettings.showSignatureLines}
                      onChange={(e) => setFormSettings(prev => ({ ...prev, showSignatureLines: e.target.checked }))}
                    />
                    Show Signature Lines
                  </label>
                </div>
              </div>
            </div>

            <div className="settings-section">
              <h3>Saved Settings</h3>
              <div className="saved-settings">
                {savedSettings.map((saved, index) => (
                  <div key={index} className="saved-setting-item">
                    <span>{saved.name}</span>
                    <button 
                      className="toolkit-btn toolkit-btn--secondary toolkit-btn--compact"
                      onClick={() => onLoadSettings(saved.name)}
                    >
                      Load
                    </button>
                  </div>
                ))}
              </div>
              <div className="save-new">
                <input 
                  type="text"
                  placeholder="Settings name..."
                  value={settingsName}
                  onChange={(e) => setSettingsName(e.target.value)}
                />
                <button type="button" className="toolkit-btn toolkit-btn--primary toolkit-btn--compact" onClick={handleSaveAs}>
                  Save as
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button type="button" className="toolkit-btn toolkit-btn--ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="toolkit-btn toolkit-btn--primary" onClick={handleSave}>Apply settings</button>
        </div>
      </div>
    </div>
  );
}

// Batch Print Modal
function BatchPrintModal({ settings, onSettingsChange, onPrint, onClose, orderCount }) {
  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Batch Print Configuration</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          <div className="batch-info">
            <p><strong>Orders to print:</strong> {orderCount}</p>
          </div>

          <div className="batch-settings">
            <div className="form-field">
              <label>Group By</label>
              <select 
                value={settings.groupBy}
                onChange={(e) => onSettingsChange(prev => ({ ...prev, groupBy: e.target.value }))}
              >
                <option value="none">No Grouping</option>
                <option value="shipping">Shipping Method</option>
                <option value="date">Delivery Date</option>
                <option value="company">Company</option>
              </select>
            </div>

            <div className="form-field">
              <label>Sort By</label>
              <select 
                value={settings.sortBy}
                onChange={(e) => onSettingsChange(prev => ({ ...prev, sortBy: e.target.value }))}
              >
                <option value="orderNumber">Order Number</option>
                <option value="date">Delivery Date</option>
                <option value="company">Company Name</option>
              </select>
            </div>

            <div className="form-field checkbox">
              <label>
                <input 
                  type="checkbox"
                  checked={settings.separatePages}
                  onChange={(e) => onSettingsChange(prev => ({ ...prev, separatePages: e.target.checked }))}
                />
                Separate Pages for Groups
              </label>
            </div>

            <div className="form-field checkbox">
              <label>
                <input 
                  type="checkbox"
                  checked={settings.includeIndex}
                  onChange={(e) => onSettingsChange(prev => ({ ...prev, includeIndex: e.target.checked }))}
                />
                Include Order Index
              </label>
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button type="button" className="toolkit-btn toolkit-btn--ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="toolkit-btn toolkit-btn--primary" onClick={onPrint}>Start batch print</button>
        </div>
      </div>
    </div>
  );
}

export default PrintingToolKit;
